{
  "blocks": [
    {
      "id": "therapy-acupuncture",
      "kind": "therapy",
      "tags": [
        "her",
        "optimization",
        "pre-cycle",
        "during-cycle",
        "evidence-based"
      ],
      "title": "Acupuncture",
      "icon": "🪡",
      "body": {
        "name": "Acupuncture",
        "category": "traditional",
        "description": "Traditional Chinese medicine technique using thin needles at specific body points to improve blood flow, reduce stress, and support hormonal regulation.",
        "howItWorks": "Acupuncture stimulates the nervous system, releasing chemicals that influence hormonal pathways and increase blood flow to the uterus. It may lower cortisol (your main stress hormone) and improve ovarian response by acting on the HPO axis (the hormonal system that controls ovulation).",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "sessionDuration": "30-60 minutes",
        "frequencyRange": "1-2 sessions per week",
        "costRange": {
          "low": 75,
          "high": 200
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "May improve blood flow to the uterus and ovaries",
            "Shown to reduce stress and anxiety during IVF cycles",
            "Some studies report improved IVF success rates when used around embryo transfer",
            "Generally relaxing with minimal side effects"
          ],
          "shouldNotExpect": [
            "This is not a standalone fertility treatment - it should be used alongside medical care",
            "Results vary widely across studies, and many have methodological limitations",
            "Some benefits may come from stress reduction rather than direct reproductive effects"
          ]
        },
        "considerations": [
          "Choose a practitioner experienced in fertility-focused acupuncture",
          "Treatment protocols vary - ask about their approach to your specific situation",
          "Some clinics offer acupuncture on-site around embryo transfer day"
        ],
        "bestFor": [
          "IVF/IUI support",
          "Stress reduction",
          "Unexplained infertility",
          "PMOS (ex PCOS)"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Improved clinical pregnancy rates when given around embryo transfer",
            "magnitude": "~65% more pregnancies vs no acupuncture (odds ratio 1.65)",
            "mechanism": "Increased uterine blood flow and reduced stress hormones via parasympathetic activation",
            "source": "Manheimer et al., BMJ 2008 (meta-analysis of 7 RCTs)",
            "sourceUrl": "https://www.bmj.com/content/336/7643/545"
          },
          {
            "direction": "neutral",
            "outcome": "Later, larger trials showed smaller or no benefit when sham acupuncture was used as control",
            "mechanism": "Suggests some benefit may come from the relaxation ritual itself rather than needle placement",
            "source": "Smith et al., JAMA 2018 (RCT, n=824)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/29800212/"
          },
          {
            "direction": "positive",
            "outcome": "Modest reduction in anxiety reported at the time of embryo transfer in a secondary analysis of the JAMA RCT",
            "mechanism": "Quality of life and longer-term anxiety did not differ from sham acupuncture",
            "source": "Smith et al., Acta Obstetricia et Gynecologica Scandinavica 2019",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/30592302/"
          }
        ],
        "headlineStat": "↑65% pregnancy rate"
      },
      "bodyFr": {
        "name": "Acupuncture",
        "category": "traditional",
        "description": "Technique issue de la médecine traditionnelle chinoise qui utilise de fines aiguilles posées à des points précis du corps pour améliorer la circulation, réduire le stress et soutenir l'équilibre hormonal.",
        "howItWorks": "L'acupuncture stimule le système nerveux et libère des substances qui influencent les voies hormonales et augmentent la circulation sanguine vers l'utérus. Elle peut faire baisser le cortisol (la principale hormone du stress) et améliorer la réponse ovarienne en agissant sur l'axe HHO (le système hormonal qui régule l'ovulation).",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "sessionDuration": "30 à 60 minutes",
        "frequencyRange": "1 à 2 séances par semaine",
        "costRange": {
          "low": 50,
          "high": 90
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Peut améliorer la circulation sanguine vers l'utérus et les ovaires",
            "A montré une réduction du stress et de l'anxiété pendant les cycles de FIV",
            "Certaines études rapportent un meilleur taux de réussite de FIV quand elle est pratiquée autour du transfert d'embryon",
            "Globalement relaxante, avec peu d'effets secondaires"
          ],
          "shouldNotExpect": [
            "Ce n'est pas un traitement de fertilité à part entière : elle vient en complément du suivi médical",
            "Les résultats varient beaucoup d'une étude à l'autre et beaucoup ont des limites méthodologiques",
            "Une partie des bénéfices peut venir de la réduction du stress plutôt que d'un effet reproductif direct"
          ]
        },
        "considerations": [
          "Choisis un·e praticien·ne expérimenté·e en acupuncture orientée fertilité (sage-femme ou médecin acupuncteur)",
          "Les protocoles varient : pose des questions sur l'approche adaptée à ta situation",
          "Certaines cliniques proposent l'acupuncture sur place autour du jour du transfert",
          "Les séances réalisées par un·e médecin acupuncteur conventionné·e peuvent être partiellement remboursées par la Sécurité sociale"
        ],
        "bestFor": [
          "Accompagnement FIV / IIU",
          "Réduction du stress",
          "Infertilité inexpliquée",
          "SOPK (PMOS)"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Amélioration des taux de grossesse clinique quand elle est pratiquée autour du transfert d'embryon",
            "magnitude": "~65 % de grossesses en plus vs sans acupuncture (rapport de cotes 1,65)",
            "mechanism": "Meilleure circulation utérine et baisse des hormones du stress via l'activation parasympathique",
            "source": "Manheimer et al., BMJ 2008 (meta-analysis of 7 RCTs)",
            "sourceUrl": "https://www.bmj.com/content/336/7643/545"
          },
          {
            "direction": "neutral",
            "outcome": "Des essais plus récents et plus larges montrent un bénéfice plus faible voire nul quand l'acupuncture factice est utilisée comme contrôle",
            "mechanism": "Suggère qu'une partie du bénéfice vient du rituel de relaxation plutôt que de l'emplacement des aiguilles",
            "source": "Smith et al., JAMA 2018 (RCT, n=824)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/29800212/"
          },
          {
            "direction": "positive",
            "outcome": "Légère réduction de l'anxiété rapportée au moment du transfert d'embryon dans une analyse secondaire de l'essai JAMA",
            "mechanism": "La qualité de vie et l'anxiété à plus long terme ne diffèrent pas de l'acupuncture factice",
            "source": "Smith et al., Acta Obstetricia et Gynecologica Scandinavica 2019",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/30592302/"
          }
        ],
        "headlineStat": "↑65 % de grossesse"
      },
      "sources": [
        {
          "title": "Effects of acupuncture on rates of pregnancy and live birth among women undergoing in vitro fertilisation",
          "journal": "BMJ",
          "year": 2008,
          "evidenceLevel": "meta-analysis",
          "url": "https://www.bmj.com/content/336/7643/545"
        },
        {
          "title": "Acupuncture and IVF: recent randomized controlled trials and meta-analyses",
          "journal": "Human Reproduction Update",
          "year": 2019,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-chinese-herbal-medicine",
      "kind": "therapy",
      "tags": ["her", "optimization", "pre-cycle"],
      "title": "Chinese Herbal Medicine",
      "icon": "🌿",
      "body": {
        "name": "Chinese Herbal Medicine",
        "category": "traditional",
        "description": "Customized herbal formulas prescribed by trained practitioners to regulate menstrual cycles, support ovarian function, and improve overall reproductive health.",
        "howItWorks": "Herbal formulas are tailored to individual patterns diagnosed through traditional Chinese medicine. Commonly used herbs may influence how the body processes estrogen, improve blood supply to the ovaries, and regulate the menstrual cycle.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "frequencyRange": "Daily herbal formulas for 3-6 months",
        "costRange": {
          "low": 100,
          "high": 300
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Personalized herbal formulas based on your individual diagnosis",
            "Some studies show improved pregnancy rates when combined with IVF",
            "May help regulate irregular menstrual cycles"
          ],
          "shouldNotExpect": [
            "Quality and standardization of herbal products varies significantly between suppliers",
            "These must be prescribed by a qualified practitioner - self-prescribing can be harmful",
            "Some herbs may interact with fertility medications, so always tell your fertility doctor"
          ]
        },
        "considerations": [
          "Always inform your fertility doctor about any herbal supplements",
          "Stop all herbs before starting an IVF stimulation cycle unless your fertility doctor approves",
          "Look for practitioners certified in Chinese herbal medicine"
        ],
        "bestFor": [
          "Cycle regulation",
          "Pre-conception preparation",
          "Unexplained infertility"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "In a meta-analysis of 40 RCTs, Chinese herbal medicine roughly doubled pregnancy rates compared with Western fertility drug therapy over 3-6 months",
            "magnitude": "Average pregnancy rate 60% vs 33% (risk ratio 1.74, 95% CI 1.56-1.94)",
            "mechanism": "Authors suggest the herbs may regulate the HPO axis (the hormonal system that controls ovulation) and improve blood supply to the ovaries and uterus, but these mechanisms have not been directly demonstrated",
            "source": "Ried, Complementary Therapies in Medicine 2015 (meta-analysis, 40 RCTs, n=4,247)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/25637159/"
          },
          {
            "direction": "positive",
            "outcome": "Combining Chinese herbal medicine with IVF was associated with higher clinical pregnancy and ongoing pregnancy rates compared with IVF alone",
            "magnitude": "Clinical pregnancy ~2× more likely (odds ratio 2.04, 95% CI 1.67-2.49); ongoing pregnancy ~1.9× (odds ratio 1.91, 95% CI 1.17-3.10)",
            "source": "Cao et al., PLOS ONE 2013 (meta-analysis, 20 RCTs, n=1,721)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC3858252/"
          },
          {
            "direction": "neutral",
            "outcome": "Both meta-analyses flag a high risk of bias in the underlying trials (no blinding, small samples, mostly Chinese-language journals), so authors say the magnitude of benefit is unlikely to be accurate and large placebo-controlled trials are still needed",
            "source": "Cao et al., PLOS ONE 2013 (meta-analysis, 20 RCTs, n=1,721)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC3858252/"
          }
        ],
        "headlineStat": "↑pregnancy rate, low-quality evidence"
      },
      "bodyFr": {
        "name": "Phytothérapie chinoise",
        "category": "traditional",
        "description": "Formules à base de plantes médicinales prescrites par des praticien·nes formé·es pour réguler le cycle, soutenir la fonction ovarienne et améliorer la santé reproductive globale.",
        "howItWorks": "Les formules sont adaptées à chaque profil après un bilan de médecine traditionnelle chinoise. Les plantes utilisées peuvent agir sur le métabolisme des œstrogènes, améliorer la circulation ovarienne et réguler le cycle menstruel.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "frequencyRange": "Prise quotidienne pendant 3 à 6 mois",
        "costRange": {
          "low": 80,
          "high": 200
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Des formules personnalisées en fonction de ton bilan individuel",
            "Certaines études montrent un meilleur taux de grossesse en complément de la FIV",
            "Peut aider à régulariser un cycle irrégulier"
          ],
          "shouldNotExpect": [
            "La qualité et la standardisation des produits varient beaucoup d'un fournisseur à l'autre",
            "Ces préparations doivent être prescrites par un·e praticien·ne qualifié·e : s'auto-prescrire peut être dangereux",
            "Certaines plantes peuvent interagir avec les traitements de fertilité, signale toujours leur usage à ton médecin"
          ]
        },
        "considerations": [
          "Informe systématiquement ton·ta médecin de la PMA de toute prise de plantes",
          "Arrête toute phytothérapie avant le démarrage d'un cycle de stimulation FIV, sauf accord explicite",
          "Privilégie des praticien·nes certifié·es en phytothérapie chinoise"
        ],
        "bestFor": [
          "Régulation du cycle",
          "Préparation pré-conception",
          "Infertilité inexpliquée"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Dans une méta-analyse de 40 essais randomisés, la phytothérapie chinoise a environ doublé les taux de grossesse vs traitement médical occidental sur 3 à 6 mois",
            "magnitude": "Taux de grossesse moyen 60 % vs 33 % (risque relatif 1,74, IC 95 % 1,56-1,94)",
            "mechanism": "Les auteurs évoquent une régulation de l'axe HHO (le système hormonal qui régule l'ovulation) et une meilleure circulation ovarienne et utérine, mais ces mécanismes ne sont pas démontrés directement",
            "source": "Ried, Complementary Therapies in Medicine 2015 (meta-analysis, 40 RCTs, n=4,247)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/25637159/"
          },
          {
            "direction": "positive",
            "outcome": "Associer phytothérapie chinoise et FIV a été associé à des taux de grossesse clinique et évolutive plus élevés que la FIV seule",
            "magnitude": "Grossesse clinique ~2× plus probable (rapport de cotes 2,04, IC 95 % 1,67-2,49) ; grossesse évolutive ~1,9× (rapport de cotes 1,91, IC 95 % 1,17-3,10)",
            "source": "Cao et al., PLOS ONE 2013 (meta-analysis, 20 RCTs, n=1,721)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC3858252/"
          },
          {
            "direction": "neutral",
            "outcome": "Les deux méta-analyses signalent un risque élevé de biais dans les essais inclus (pas de double aveugle, petits effectifs, journaux essentiellement en chinois) ; les auteurs disent que l'ampleur du bénéfice est probablement surestimée et qu'il faut de grands essais placebo-contrôlés",
            "source": "Cao et al., PLOS ONE 2013 (meta-analysis, 20 RCTs, n=1,721)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC3858252/"
          }
        ],
        "headlineStat": "↑grossesse, preuves de faible qualité"
      },
      "sources": [
        {
          "title": "Chinese herbal medicine for subfertility in women: a Cochrane review",
          "journal": "Cochrane Database of Systematic Reviews",
          "year": 2011,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-mbsr",
      "kind": "therapy",
      "tags": [
        "both-partners",
        "optimization",
        "pre-cycle",
        "during-cycle",
        "evidence-based"
      ],
      "title": "Mindfulness-Based Stress Reduction",
      "icon": "🧘",
      "body": {
        "name": "Mindfulness-Based Stress Reduction (MBSR)",
        "category": "mind-body",
        "description": "An 8-week structured program combining mindfulness meditation, body awareness, and yoga to reduce stress and improve emotional wellbeing during fertility treatment.",
        "howItWorks": "MBSR lowers cortisol (your main stress hormone) and activates the calming parasympathetic nervous system, counteracting the chronic stress response that can interfere with reproductive hormones. Regular practice improves emotional regulation, sleep quality, and overall quality of life.",
        "evidenceStrength": "moderate",
        "forWhom": "both",
        "sessionDuration": "45-90 minutes",
        "frequencyRange": "Daily practice (20-45 min) + weekly group sessions",
        "costRange": {
          "low": 200,
          "high": 600
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Clinically proven to reduce anxiety, depression, and perceived stress",
            "May improve quality of life during IVF treatment",
            "Teaches lasting skills for managing the emotional toll of fertility treatment",
            "Some studies show improved pregnancy rates in mind-body program participants"
          ],
          "shouldNotExpect": [
            "This is not a quick fix - benefits build over weeks of consistent practice",
            "Stress reduction alone may not resolve underlying medical causes of infertility",
            "Group programs require a significant time commitment (8 weekly sessions + daily practice)"
          ]
        },
        "considerations": [
          "Many fertility clinics now offer mind-body programs specifically for patients",
          "Apps like Headspace and Calm offer guided fertility-specific meditations",
          "Can be combined with any medical treatment without contraindications"
        ],
        "bestFor": [
          "IVF/IUI emotional support",
          "Anxiety management",
          "Recurrent loss",
          "Long treatment journeys"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "In an RCT of 143 women under 40 starting IVF, those assigned to a 10-session mind-body program had higher pregnancy rates in their second IVF cycle (when most had attended at least half the sessions)",
            "magnitude": "Cycle 2 pregnancy 52% mind-body vs 20% control; no difference in cycle 1 (43% vs 43%)",
            "source": "Domar et al., Fertility and Sterility 2011 (RCT, n=143)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/21496800/"
          },
          {
            "direction": "positive",
            "outcome": "Psychosocial interventions for couples in fertility treatment, including mind-body programs, reduce psychological distress and may improve clinical pregnancy rates",
            "mechanism": "Parasympathetic activation and cognitive reframing reduce the chronic stress response",
            "source": "Frederiksen et al., BMJ Open 2015 (systematic review and meta-analysis)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/25631310/"
          }
        ],
        "headlineStat": "↑52% pregnancy rate"
      },
      "bodyFr": {
        "name": "Réduction du stress basée sur la pleine conscience (MBSR)",
        "category": "mind-body",
        "description": "Programme structuré de 8 semaines associant méditation de pleine conscience, conscience corporelle et yoga, pour réduire le stress et améliorer le bien-être émotionnel pendant un parcours de fertilité.",
        "howItWorks": "Le MBSR fait baisser le cortisol (la principale hormone du stress) et active le système nerveux parasympathique (la branche apaisante du système nerveux), ce qui contrebalance le stress chronique qui peut perturber les hormones reproductives. La pratique régulière améliore la régulation émotionnelle, la qualité du sommeil et la qualité de vie globale.",
        "evidenceStrength": "moderate",
        "forWhom": "both",
        "sessionDuration": "45 à 90 minutes",
        "frequencyRange": "Pratique quotidienne (20 à 45 min) + séances de groupe hebdomadaires",
        "costRange": {
          "low": 250,
          "high": 500
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Démontré cliniquement pour réduire l'anxiété, la dépression et le stress perçu",
            "Peut améliorer la qualité de vie pendant la FIV",
            "T'apprend des compétences durables pour gérer la charge émotionnelle du parcours",
            "Certaines études montrent un meilleur taux de grossesse chez les participantes à un programme corps-esprit"
          ],
          "shouldNotExpect": [
            "Ce n'est pas une solution rapide : les bénéfices se construisent sur plusieurs semaines de pratique régulière",
            "La gestion du stress seule ne résout pas les causes médicales sous-jacentes",
            "Les programmes en groupe demandent un vrai engagement (8 séances hebdomadaires + pratique quotidienne)"
          ]
        },
        "considerations": [
          "De plus en plus de cliniques de fertilité proposent des programmes corps-esprit dédiés à leurs patient·es",
          "Des applis comme Petit BamBou, Headspace ou Calm proposent des méditations guidées spécifiques à la fertilité",
          "Peut être combiné avec n'importe quel traitement médical, sans contre-indication"
        ],
        "bestFor": [
          "Accompagnement émotionnel FIV / IIU",
          "Gestion de l'anxiété",
          "Fausses couches à répétition",
          "Parcours longs"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Dans un essai randomisé chez 143 femmes de moins de 40 ans débutant une FIV, celles assignées à un programme corps-esprit de 10 séances ont eu de meilleurs taux de grossesse au 2e cycle de FIV (au moment où la plupart avaient assisté à au moins la moitié des séances)",
            "magnitude": "Grossesse cycle 2 : 52 % corps-esprit vs 20 % contrôle ; pas de différence au cycle 1 (43 % vs 43 %)",
            "source": "Domar et al., Fertility and Sterility 2011 (RCT, n=143)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/21496800/"
          },
          {
            "direction": "positive",
            "outcome": "Les interventions psychosociales pour les couples en parcours de fertilité, y compris les programmes corps-esprit, réduisent la détresse psychologique et peuvent améliorer le taux de grossesse clinique",
            "mechanism": "L'activation parasympathique et le recadrage cognitif réduisent la réponse au stress chronique",
            "source": "Frederiksen et al., BMJ Open 2015 (revue systématique et méta-analyse)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/25631310/"
          }
        ],
        "headlineStat": "↑52 % de grossesse"
      },
      "sources": [
        {
          "title": "A mind-body program for infertility patients: impact on pregnancy rates and mood",
          "journal": "Fertility and Sterility",
          "year": 2011,
          "evidenceLevel": "rct"
        },
        {
          "title": "Mindfulness-based interventions for fertility: a systematic review",
          "journal": "Human Reproduction Update",
          "year": 2020,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-yoga",
      "kind": "therapy",
      "tags": ["her", "optimization", "pre-cycle", "during-cycle"],
      "title": "Fertility Yoga",
      "icon": "🧘‍♀️",
      "body": {
        "name": "Fertility Yoga",
        "category": "mind-body",
        "description": "Gentle yoga practices specifically designed to support reproductive health through stress reduction, improved pelvic blood flow, and hormonal balance.",
        "howItWorks": "Fertility yoga combines gentle poses that increase blood flow to the reproductive organs, breathing techniques that activate the parasympathetic nervous system, and restorative postures that reduce cortisol. It avoids intense core work or inversions that may be contraindicated during treatment.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "sessionDuration": "30-60 minutes",
        "frequencyRange": "2-4 sessions per week",
        "costRange": {
          "low": 15,
          "high": 30
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Reduced stress and improved emotional wellbeing",
            "Gentle movement that supports blood flow to the pelvis",
            "Community and support from other women on similar journeys"
          ],
          "shouldNotExpect": [
            "You should avoid hot yoga, intense core work, and deep twists during treatment cycles",
            "There is limited clinical evidence specifically for fertility outcomes",
            "Yoga is not a substitute for medical fertility treatment"
          ]
        },
        "considerations": [
          "Look for instructors with fertility-specific training",
          "Modify or pause practice during the two-week wait and early pregnancy",
          "Restorative and yin yoga styles are generally preferred over vinyasa or power yoga"
        ],
        "bestFor": [
          "Stress reduction",
          "General wellbeing",
          "Pelvic blood flow",
          "Community support"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "A prospective 6-week Hatha Yoga program in women awaiting IVF was associated with lower anxiety and depression and improved fertility-related quality of life over time",
            "mechanism": "Breathing exercises (pranayama) and restorative poses are proposed to activate the vagus nerve and shift the body toward its 'rest and digest' state",
            "source": "Oron et al., Reproductive BioMedicine Online 2015 (prospective uncontrolled study, n=49)",
            "sourceUrl": "https://www.rbmojournal.com/article/S1472-6483(15)00057-7/fulltext"
          },
          {
            "direction": "positive",
            "outcome": "Yoga reduces psychological distress in women receiving infertility treatment",
            "source": "Effect of Yoga on Psychological Distress among Women Receiving Treatment for Infertility, 2020",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/32669765/"
          }
        ],
        "headlineStat": "↓ stress & cortisol"
      },
      "bodyFr": {
        "name": "Yoga fertilité",
        "category": "mind-body",
        "description": "Pratiques de yoga doux pensées pour soutenir la santé reproductive : réduction du stress, meilleure circulation pelvienne et équilibre hormonal.",
        "howItWorks": "Le yoga fertilité combine des postures douces qui favorisent la circulation vers les organes reproducteurs, des techniques de respiration qui activent le système parasympathique, et des postures restauratrices qui font baisser le cortisol. Il évite les postures abdominales intenses et les inversions, qui peuvent être déconseillées pendant un traitement.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "sessionDuration": "30 à 60 minutes",
        "frequencyRange": "2 à 4 séances par semaine",
        "costRange": {
          "low": 15,
          "high": 25
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Une baisse du stress et un meilleur bien-être émotionnel",
            "Des mouvements doux qui soutiennent la circulation pelvienne",
            "Une communauté et un soutien avec d'autres femmes sur le même parcours"
          ],
          "shouldNotExpect": [
            "Évite le yoga chaud, les abdos intenses et les torsions profondes pendant les cycles de traitement",
            "Les preuves cliniques spécifiques aux résultats de fertilité restent limitées",
            "Le yoga ne remplace pas un traitement médical de fertilité"
          ]
        },
        "considerations": [
          "Cherche des professeur·es formé·es spécifiquement à la fertilité",
          "Adapte ou interromps la pratique pendant les deux semaines d'attente et le début de grossesse",
          "Les styles restauratif et yin sont généralement préférés au vinyasa ou au power yoga"
        ],
        "bestFor": [
          "Réduction du stress",
          "Bien-être général",
          "Circulation pelvienne",
          "Soutien communautaire"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Une étude prospective de 6 semaines de Hatha Yoga chez des femmes en attente de FIV a été associée à une baisse de l'anxiété et de la dépression et à une amélioration de la qualité de vie liée à la fertilité au cours du temps",
            "mechanism": "Les exercices de respiration (pranayama) et les postures restauratrices sont supposés activer le nerf vague et faire basculer le corps vers son état de repos et de récupération",
            "source": "Oron et al., Reproductive BioMedicine Online 2015 (étude prospective non contrôlée, n=49)",
            "sourceUrl": "https://www.rbmojournal.com/article/S1472-6483(15)00057-7/fulltext"
          },
          {
            "direction": "positive",
            "outcome": "Le yoga réduit la détresse psychologique chez les femmes en parcours de traitement de l'infertilité",
            "source": "Effect of Yoga on Psychological Distress among Women Receiving Treatment for Infertility, 2020",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/32669765/"
          }
        ],
        "headlineStat": "↓ stress & cortisol"
      },
      "sources": [
        {
          "title": "Yoga and fertility: a systematic review of the evidence",
          "journal": "Journal of Integrative Medicine",
          "year": 2021,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-cbt",
      "kind": "therapy",
      "tags": [
        "both-partners",
        "optimization",
        "pre-cycle",
        "during-cycle",
        "evidence-based"
      ],
      "title": "Cognitive Behavioral Therapy (CBT)",
      "icon": "🧠",
      "body": {
        "name": "Cognitive Behavioral Therapy (CBT)",
        "category": "mind-body",
        "description": "A structured, evidence-based psychological therapy that helps identify and change negative thought patterns and behaviors associated with infertility distress.",
        "howItWorks": "CBT helps patients recognize cognitive distortions (catastrophizing, all-or-nothing thinking) common during fertility treatment and replace them with more balanced perspectives. It provides concrete coping strategies and reduces the psychological burden that can interfere with treatment adherence and quality of life.",
        "evidenceStrength": "strong",
        "forWhom": "both",
        "sessionDuration": "50-60 minutes",
        "frequencyRange": "Weekly sessions for 8-12 weeks",
        "costRange": {
          "low": 100,
          "high": 250
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Clinically proven to reduce anxiety and depression in infertility patients",
            "Provides practical tools for managing the emotional toll of treatment",
            "Improves relationship satisfaction and communication between partners",
            "Some studies suggest reduced dropout rates from fertility treatment"
          ],
          "shouldNotExpect": [
            "CBT is not a direct fertility treatment - it addresses psychological wellbeing",
            "It requires active participation and homework between sessions",
            "Benefits accumulate over several sessions, not immediately"
          ]
        },
        "considerations": [
          "Look for therapists specializing in reproductive mental health",
          "Many therapists now offer virtual CBT sessions",
          "Can be combined with any medical fertility treatment",
          "Insurance often covers CBT for anxiety or depression diagnosis"
        ],
        "bestFor": [
          "Anxiety during treatment",
          "Depression",
          "Relationship strain",
          "Treatment decision-making"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Psychosocial interventions, in particular CBT, reduce psychological distress and may improve clinical pregnancy rates in couples in fertility treatment",
            "mechanism": "Restructuring cognitive distortions (catastrophizing, self-blame) reduces the chronic psychological stress that can impair treatment adherence",
            "source": "Frederiksen et al., BMJ Open 2015 (systematic review and meta-analysis)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/25631310/"
          },
          {
            "direction": "positive",
            "outcome": "CBT significantly reduces depression and anxiety in infertile women and is at least as effective as fluoxetine for reducing infertility-related stress",
            "source": "Faramarzi et al., International Journal of Fertility and Sterility 2013 (RCT, n=89)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/24520487/"
          }
        ],
        "headlineStat": "↓ anxiety & depression"
      },
      "bodyFr": {
        "name": "Thérapie cognitivo-comportementale (TCC)",
        "category": "mind-body",
        "description": "Thérapie psychologique structurée et fondée sur les preuves, qui aide à identifier et modifier les schémas de pensée et les comportements négatifs liés à la détresse de l'infertilité.",
        "howItWorks": "La TCC apprend à repérer les distorsions cognitives (catastrophisme, pensée en tout-ou-rien) fréquentes pendant un parcours de fertilité, et à les remplacer par des perspectives plus équilibrées. Elle propose des stratégies concrètes pour faire face et réduire la charge psychologique qui peut entraver l'observance du traitement et la qualité de vie.",
        "evidenceStrength": "strong",
        "forWhom": "both",
        "sessionDuration": "50 à 60 minutes",
        "frequencyRange": "Séances hebdomadaires pendant 8 à 12 semaines",
        "costRange": {
          "low": 60,
          "high": 90
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Démontré cliniquement pour réduire l'anxiété et la dépression chez les patient·es en infertilité",
            "T'apporte des outils concrets pour gérer la charge émotionnelle du parcours",
            "Améliore la satisfaction du couple et la communication entre partenaires",
            "Certaines études suggèrent moins d'abandons en cours de traitement"
          ],
          "shouldNotExpect": [
            "La TCC n'est pas un traitement direct de la fertilité : elle agit sur le bien-être psychologique",
            "Demande une participation active et des exercices entre les séances",
            "Les bénéfices se construisent sur plusieurs séances, pas immédiatement"
          ]
        },
        "considerations": [
          "Cherche un·e psychologue spécialisé·e en santé mentale périnatale ou reproductive",
          "Beaucoup de psychologues proposent désormais des séances en visio",
          "Peut être combinée avec n'importe quel traitement médical de fertilité",
          "Les séances chez un·e psychologue conventionné·e via le dispositif Mon Soutien Psy peuvent être partiellement remboursées"
        ],
        "bestFor": [
          "Anxiété pendant le traitement",
          "Dépression",
          "Tensions de couple",
          "Prises de décision médicales"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Les interventions psychosociales, en particulier la TCC, réduisent la détresse psychologique et peuvent améliorer le taux de grossesse clinique chez les couples en parcours de fertilité",
            "mechanism": "La restructuration des distorsions cognitives (catastrophisme, auto-accusation) réduit le stress psychologique chronique qui peut nuire à l'observance du traitement",
            "source": "Frederiksen et al., BMJ Open 2015 (revue systématique et méta-analyse)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/25631310/"
          },
          {
            "direction": "positive",
            "outcome": "La TCC réduit significativement la dépression et l'anxiété chez les femmes en infertilité et est au moins aussi efficace que la fluoxétine pour réduire le stress lié à l'infertilité",
            "source": "Faramarzi et al., International Journal of Fertility and Sterility 2013 (essai randomisé, n=89)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/24520487/"
          }
        ],
        "headlineStat": "↓ anxiété et dépression"
      },
      "sources": [
        {
          "title": "Cognitive-behavioral therapy for infertility-related distress: a meta-analysis",
          "journal": "Psychology & Health",
          "year": 2019,
          "evidenceLevel": "meta-analysis"
        },
        {
          "title": "Psychological interventions for infertile patients: a Cochrane review",
          "journal": "Cochrane Database of Systematic Reviews",
          "year": 2016,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-endometrial-scratching",
      "kind": "therapy",
      "tags": ["her", "during-cycle", "evidence-based"],
      "title": "Endometrial Scratching",
      "icon": "🔬",
      "body": {
        "name": "Endometrial Scratching",
        "category": "clinical",
        "description": "A procedure where the uterine lining is deliberately scratched or biopsied in the cycle before embryo transfer, with the theory that the healing response improves implantation.",
        "howItWorks": "The scratch causes a controlled inflammatory response in the endometrium. The theory is that the healing process releases growth factors and cytokines that make the lining more receptive to embryo implantation. The procedure is typically done in the luteal phase of the cycle preceding transfer.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "sessionDuration": "5-15 minutes",
        "costRange": {
          "low": 200,
          "high": 500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A quick office procedure - no anesthesia usually required",
            "May cause cramping during and after the procedure",
            "Some earlier studies showed improved implantation rates"
          ],
          "shouldNotExpect": [
            "The largest RCT (2019) found no benefit - the evidence is now considered mixed at best",
            "Most major fertility societies do not recommend this as routine practice",
            "Some clinics may still offer it despite weakened evidence"
          ]
        },
        "considerations": [
          "Ask your doctor about the latest evidence before agreeing to this procedure",
          "The landmark 2019 trial in the NEJM changed clinical recommendations",
          "Some subgroups (recurrent implantation failure) may still benefit - discuss with your RE",
          "Cost is typically out-of-pocket as most insurers consider it experimental"
        ],
        "bestFor": [
          "Recurrent implantation failure (debated)",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "The largest RCT to date in women undergoing IVF found no improvement in live birth rates with endometrial scratching",
            "magnitude": "Live birth 26.1% in scratch group vs 26.1% control - no difference",
            "mechanism": "While originally hypothesized to trigger a healing response that improves implantation via cytokines, the controlled trial showed this does not translate to clinical benefit",
            "source": "Lensen et al., New England Journal of Medicine 2019 (RCT, n=1,364)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/30673547/"
          },
          {
            "direction": "neutral",
            "outcome": "The SCRaTCH trial, in women with one previous failed IVF/ICSI cycle, found a small (4.6%) absolute increase in live birth in the scratch group, but the confidence interval crossed zero, so the authors concluded scratching should not be performed outside research",
            "magnitude": "Live birth 23.7% scratch vs 19.1% control; 95% CI -0.7% to +9.9%",
            "source": "van Hoogenhuijze et al., Human Reproduction 2021 (SCRaTCH trial, n=933)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33289528/"
          },
          {
            "direction": "neutral",
            "outcome": "The updated Cochrane review concludes the effect of endometrial scratching on live birth and clinical pregnancy in IVF is unclear, with results compatible with a small benefit, no effect, or a small harm",
            "magnitude": "Live birth OR 1.12 (95% CI 0.98-1.28), moderate-certainty evidence",
            "source": "Lensen et al., Cochrane Database of Systematic Reviews 2021 (37 RCTs)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/34110001/"
          }
        ],
        "headlineStat": "No impact on live birth"
      },
      "bodyFr": {
        "name": "Scratching endométrial (éraflure de l'endomètre)",
        "category": "clinical",
        "description": "Procédure qui consiste à gratter délibérément (ou à biopsier) la muqueuse utérine pendant le cycle précédant le transfert d'embryon, avec l'idée que la réponse de cicatrisation améliore l'implantation. En France, la pratique a été largement abandonnée dans les centres d'AMP après les essais négatifs de 2019 (Lensen, NEJM). Certains praticien·nes la proposent encore dans des cas très sélectionnés (échecs d'implantation à répétition), mais ce n'est pas un acte du parcours AMP standard et il n'est généralement pas remboursé.",
        "howItWorks": "Le scratch provoque une réponse inflammatoire contrôlée de l'endomètre. La théorie est que la cicatrisation libère des facteurs de croissance et des cytokines qui rendent la muqueuse plus réceptive à l'implantation. La procédure est généralement réalisée en phase lutéale du cycle qui précède le transfert. Geste rapide en consultation, sans anesthésie, parfois douloureux.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "sessionDuration": "5 à 15 minutes",
        "costRange": {
          "low": 150,
          "high": 350
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Une procédure rapide en cabinet, généralement sans anesthésie",
            "Peut provoquer des crampes pendant et après, parfois assez douloureuses",
            "Largement délaissé dans les centres d'AMP français depuis 2019 ; encore proposé ponctuellement en cas d'échecs d'implantation répétés"
          ],
          "shouldNotExpect": [
            "Le plus grand essai (Lensen, NEJM 2019) n'a montré aucun bénéfice : les preuves sont aujourd'hui considérées comme négatives ou mitigées au mieux",
            "Ni l'ESHRE ni les sociétés savantes françaises ne recommandent cette procédure en routine",
            "Ce n'est pas un acte intégré au panier de soins AMP remboursé par l'Assurance Maladie"
          ]
        },
        "considerations": [
          "Statut en France : largement abandonné en centre public d'AMP après les essais négatifs de 2019",
          "Demande à ton·ta médecin les dernières données avant d'accepter la procédure : beaucoup de praticien·nes français·es ne le proposent plus",
          "Certains sous-groupes (échecs d'implantation à répétition) pourraient théoriquement en bénéficier, mais les données restent insuffisantes",
          "Non remboursé en France : reste à ta charge quand il est encore proposé"
        ],
        "bestFor": [
          "Échecs d'implantation à répétition (controversé, hors recommandation)",
          "Non recommandé comme add-on FIV de routine ; largement abandonné en France"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "Le plus grand essai randomisé à ce jour, chez des femmes en FIV, n'a montré aucune amélioration du taux de naissance vivante avec le scratching endométrial",
            "magnitude": "Naissance vivante 26,1 % avec scratch vs 26,1 % sans : aucune différence",
            "mechanism": "L'hypothèse initiale était que le scratch déclenche une réponse cicatricielle améliorant l'implantation via les cytokines, mais l'essai contrôlé montre que ça ne se traduit pas par un bénéfice clinique",
            "source": "Lensen et al., New England Journal of Medicine 2019 (RCT, n=1,364)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/30673547/"
          },
          {
            "direction": "neutral",
            "outcome": "L'essai SCRaTCH, chez des femmes après un cycle de FIV/ICSI en échec, a trouvé une petite augmentation absolue (4,6 %) de la naissance vivante avec le scratch, mais l'intervalle de confiance traversait zéro. Les auteurs concluent qu'il ne doit pas être réalisé hors recherche",
            "magnitude": "Naissance vivante 23,7 % scratch vs 19,1 % contrôle ; IC 95 % -0,7 % à +9,9 %",
            "source": "van Hoogenhuijze et al., Human Reproduction 2021 (SCRaTCH trial, n=933)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33289528/"
          },
          {
            "direction": "neutral",
            "outcome": "La revue Cochrane à jour conclut que l'effet du scratching sur la naissance vivante et la grossesse clinique en FIV reste incertain : les résultats sont compatibles avec un petit bénéfice, aucun effet, ou un léger préjudice",
            "magnitude": "Naissance vivante OR 1,12 (IC 95 % 0,98-1,28), preuves de qualité modérée",
            "source": "Lensen et al., Cochrane Database of Systematic Reviews 2021 (37 RCTs)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/34110001/"
          }
        ],
        "headlineStat": "Largement abandonné en France"
      },
      "sources": [
        {
          "title": "Endometrial scratching for women undergoing a first embryo transfer: the SCRaTCH RCT",
          "journal": "New England Journal of Medicine",
          "year": 2019,
          "evidenceLevel": "rct"
        },
        {
          "title": "Endometrial injury in women undergoing assisted reproductive techniques: a Cochrane review update",
          "journal": "Cochrane Database of Systematic Reviews",
          "year": 2021,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-assisted-hatching",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Assisted Hatching",
      "icon": "🥚",
      "body": {
        "name": "Assisted Hatching",
        "category": "clinical",
        "description": "A lab technique where a small opening is made in the embryo's outer shell (zona pellucida) just before transfer, with the goal of helping the embryo implant.",
        "howItWorks": "Using a laser, acid, or mechanical technique, embryologists create a small breach in the zona pellucida. The theory is that some embryos, particularly those from older patients or frozen embryos with hardened shells, may struggle to hatch naturally. The opening may facilitate implantation.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "costRange": {
          "low": 500,
          "high": 1500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Performed in the lab before embryo transfer - no additional procedure for you",
            "May improve implantation rates in specific populations",
            "Commonly offered for women over 38, frozen embryo transfers, or after failed cycles"
          ],
          "shouldNotExpect": [
            "The benefit is not proven for all patients - discuss your specific situation with your RE",
            "There is a very small risk of embryo damage during the procedure",
            "There is a slightly increased risk of identical twinning"
          ]
        },
        "considerations": [
          "Most beneficial for patients over 38 or with previous failed implantation",
          "Ask your embryology lab about their specific technique (laser is most common)",
          "Typically added as an extra cost on top of IVF fees"
        ],
        "bestFor": [
          "Advanced maternal age",
          "Frozen embryo transfer",
          "Previous failed implantation",
          "Thick zona pellucida"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "The updated Cochrane review found assisted hatching may slightly increase clinical pregnancy rates, but the authors were uncertain whether it improved live birth",
            "magnitude": "Clinical pregnancy OR 1.20 (95% CI 1.09-1.33); live birth OR 1.09 (95% CI 0.92-1.29, not significant)",
            "mechanism": "Laser thinning of the zona pellucida was proposed to help the embryo break free and implant, but pregnancy rate improvement did not consistently translate to take-home baby rates",
            "source": "Lacey et al., Cochrane Database of Systematic Reviews 2021 (39 RCTs, n=7,249)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33730422/"
          },
          {
            "direction": "negative",
            "outcome": "Assisted hatching may slightly increase multiple pregnancy rates",
            "magnitude": "Multiple pregnancy OR 1.38 (95% CI 1.13-1.68, 18 studies)",
            "source": "Lacey et al., Cochrane Database of Systematic Reviews 2021 (39 RCTs, n=7,249)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33730422/"
          }
        ],
        "headlineStat": "Uncertain effect on live birth"
      },
      "bodyFr": {
        "name": "Éclosion assistée (assisted hatching)",
        "category": "clinical",
        "description": "Technique de laboratoire qui consiste à créer une petite ouverture dans l'enveloppe externe de l'embryon (zone pellucide) juste avant le transfert, pour faciliter son implantation. En France, l'éclosion assistée est pratiquée dans de nombreux centres d'AMP, principalement sur indication médicale (âge maternel avancé, transfert d'embryon congelé, échec d'implantation antérieur, zone pellucide épaissie). Lorsqu'elle est réalisée dans le cadre d'un parcours AMP indiqué, elle est codée et prise en charge avec la FIV par l'Assurance Maladie.",
        "howItWorks": "Au laser (le plus souvent), à l'acide ou par méthode mécanique, l'embryologiste crée une petite brèche dans la zone pellucide juste avant le transfert. La théorie : certains embryons (surtout chez les patientes plus âgées ou les embryons congelés dont la coque a durci pendant la vitrification) auraient du mal à éclore naturellement. L'ouverture peut faciliter l'implantation.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "costRange": {
          "low": 0,
          "high": 0
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Réalisée au laboratoire avant le transfert : aucune procédure supplémentaire pour toi",
            "Pratiquée dans de nombreux centres d'AMP français pour des indications précises (âge avancé, TEC, échec d'implantation, zone pellucide épaissie)",
            "Prise en charge dans le parcours AMP standard de l'Assurance Maladie quand elle est indiquée",
            "Peut améliorer les taux d'implantation dans certaines populations sélectionnées"
          ],
          "shouldNotExpect": [
            "Le bénéfice n'est pas démontré pour toutes les patientes en routine : c'est une indication ciblée, pas un add-on systématique",
            "Il existe un très faible risque d'endommager l'embryon pendant la procédure",
            "Risque légèrement augmenté de jumeaux monozygotes"
          ]
        },
        "considerations": [
          "Statut en France : intégrée à la nomenclature AMP et prise en charge par l'Assurance Maladie lorsqu'elle est réalisée sur indication médicale dans un centre agréé",
          "Particulièrement proposée aux patientes de plus de 38 ans, aux TEC, ou après un antécédent d'échec d'implantation",
          "Demande à ton laboratoire la technique utilisée (le laser est la plus courante et la moins risquée pour l'embryon)",
          "Discute avec ton·ta médecin de la pertinence dans ta situation : ce n'est pas systématique"
        ],
        "bestFor": [
          "Âge maternel avancé",
          "Transfert d'embryon congelé",
          "Échec d'implantation précédent",
          "Zone pellucide épaisse"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "La revue Cochrane à jour conclut que l'éclosion assistée pourrait légèrement augmenter le taux de grossesse clinique, mais les auteurs restent incertain·es sur l'effet sur la naissance vivante",
            "magnitude": "Grossesse clinique OR 1,20 (IC 95 % 1,09-1,33) ; naissance vivante OR 1,09 (IC 95 % 0,92-1,29, non significatif)",
            "mechanism": "L'amincissement au laser de la zone pellucide était censé aider l'embryon à se libérer et à s'implanter, mais l'amélioration du taux de grossesse ne se traduit pas toujours par une naissance vivante",
            "source": "Lacey et al., Cochrane Database of Systematic Reviews 2021 (39 RCTs, n=7 249)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33730422/"
          },
          {
            "direction": "negative",
            "outcome": "L'éclosion assistée pourrait légèrement augmenter le taux de grossesses multiples",
            "magnitude": "Grossesse multiple OR 1,38 (IC 95 % 1,13-1,68 ; 18 études)",
            "source": "Lacey et al., Cochrane Database of Systematic Reviews 2021 (39 RCTs, n=7 249)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33730422/"
          }
        ],
        "headlineStat": "Effet incertain sur la naissance vivante"
      },
      "sources": [
        {
          "title": "Assisted hatching on assisted conception: a Cochrane systematic review",
          "journal": "Cochrane Database of Systematic Reviews",
          "year": 2020,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-embryo-glue",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "EmbryoGlue",
      "icon": "🧪",
      "body": {
        "name": "EmbryoGlue (Hyaluronan-Enriched Transfer Medium)",
        "category": "clinical",
        "description": "A specialized embryo transfer medium containing high concentrations of hyaluronan, a naturally occurring substance thought to help embryos adhere to the uterine lining.",
        "howItWorks": "Hyaluronan is a component of the natural fluid in the uterus at the time of implantation. EmbryoGlue mimics this environment by increasing the viscosity of the transfer medium, potentially helping the embryo stay in optimal position and attach to the endometrium.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "costRange": {
          "low": 100,
          "high": 400
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Applied during embryo transfer - no additional procedure for you",
            "A Cochrane review found a modest improvement in clinical pregnancy and live birth rates",
            "Low cost relative to other add-ons",
            "No known risks or side effects"
          ],
          "shouldNotExpect": [
            "The effect size is modest - it may help but it is not a game-changer",
            "The evidence quality is moderate, and more high-quality RCTs are needed",
            "Not all embryo transfer media are the same - ask your clinic which they use"
          ]
        },
        "considerations": [
          "One of the more evidence-supported IVF add-ons",
          "Relatively inexpensive compared to other interventions",
          "Ask your clinic if they routinely use hyaluronan-enriched medium"
        ],
        "bestFor": [
          "Fresh and frozen embryo transfers",
          "Low-cost add-on",
          "Patients wanting modest evidence-based extras"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Hyaluronic acid added to embryo transfer media probably improves clinical pregnancy and live birth rates compared with standard media (moderate-certainty evidence)",
            "magnitude": "Live birth RR 1.21 (95% CI 1.10-1.31, 10 trials, n=4,066); clinical pregnancy RR 1.16 (95% CI 1.09-1.23, 17 trials, n=5,247)",
            "mechanism": "Hyaluronan increases medium viscosity, mimicking the natural uterine environment at implantation and may help the embryo adhere to the endometrium",
            "source": "Cochrane Review, Heymann et al. 2020 (26 RCTs, n=6,704)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/32876946/"
          },
          {
            "direction": "negative",
            "outcome": "Hyaluronic acid also probably increases multiple pregnancy rates",
            "magnitude": "Multiple pregnancy RR 1.45 (95% CI 1.24-1.70, 7 trials, n=3,337)",
            "source": "Cochrane Review, Heymann et al. 2020",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/32876946/"
          }
        ],
        "headlineStat": "↑21% live birth"
      },
      "bodyFr": {
        "name": "EmbryoGlue (milieu de transfert enrichi en acide hyaluronique)",
        "category": "clinical",
        "description": "Milieu de transfert spécialisé contenant une forte concentration d'acide hyaluronique, une substance naturellement présente dans l'utérus au moment de l'implantation, qui aiderait l'embryon à adhérer à la muqueuse utérine. En France, l'EmbryoGlue est disponible dans plusieurs laboratoires d'AMP (publics et privés) ; il est classé hors nomenclature (HN) par l'Assurance Maladie, donc non remboursé. Un supplément forfaitaire est demandé au·à la patient·e quand il est utilisé.",
        "howItWorks": "L'acide hyaluronique est un composant naturel du fluide utérin au moment de l'implantation. L'EmbryoGlue mime cet environnement en augmentant la viscosité du milieu de transfert, ce qui peut aider l'embryon à rester en position optimale et à adhérer à l'endomètre. En pratique, l'embryon est plongé dans le milieu environ une heure avant le transfert ; aucun geste supplémentaire pour toi.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "costRange": {
          "low": 80,
          "high": 250
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Disponible dans de nombreux laboratoires français d'AMP, en public comme en privé",
            "Appliqué pendant le transfert d'embryon : aucune procédure supplémentaire pour toi",
            "Une revue Cochrane a trouvé une amélioration modeste des taux de grossesse clinique et de naissance vivante (surtout en transfert frais)",
            "Pas de risque ni d'effet indésirable connu"
          ],
          "shouldNotExpect": [
            "Non remboursé par la Sécurité sociale (acte hors nomenclature) : un supplément est facturé quand le milieu est utilisé",
            "L'effet est modéré : ça peut aider, mais ce n'est pas une révolution",
            "Le bénéfice est mieux établi pour les transferts frais que pour les TEC (transferts d'embryons congelés)"
          ]
        },
        "considerations": [
          "Statut en France : utilisé dans beaucoup de centres mais classé HN (hors nomenclature), donc avec un reste à charge (compter 80 à 250 € de supplément selon le laboratoire)",
          "L'un des add-ons FIV les mieux soutenus par les preuves (Cochrane 2020)",
          "Demande à ta clinique si elle utilise un milieu enrichi en acide hyaluronique, et pour quelles indications (frais vs TEC)"
        ],
        "bestFor": [
          "Transferts d'embryons frais et congelés",
          "Intervention complémentaire peu coûteuse",
          "Patient·es voulant des extras avec un bon niveau de preuve"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "L'ajout d'acide hyaluronique au milieu de transfert améliore probablement les taux de grossesse clinique et de naissance vivante (preuves de qualité modérée)",
            "magnitude": "Naissance vivante RR 1,21 (IC 95 % 1,10-1,31, 10 essais, n=4 066) ; grossesse clinique RR 1,16 (IC 95 % 1,09-1,23, 17 essais, n=5 247)",
            "mechanism": "Le hyaluronane augmente la viscosité du milieu, mimant l'environnement utérin naturel à l'implantation et pourrait aider l'embryon à adhérer à l'endomètre",
            "source": "Cochrane Review, Heymann et al. 2020 (26 RCTs, n=6 704)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/32876946/"
          },
          {
            "direction": "negative",
            "outcome": "L'acide hyaluronique augmente probablement aussi le taux de grossesses multiples",
            "magnitude": "Grossesse multiple RR 1,45 (IC 95 % 1,24-1,70, 7 essais, n=3 337)",
            "source": "Cochrane Review, Heymann et al. 2020",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/32876946/"
          }
        ],
        "headlineStat": "↑21 % de naissance vivante"
      },
      "sources": [
        {
          "title": "Hyaluronic acid-enriched transfer medium in assisted reproduction: a Cochrane review",
          "journal": "Cochrane Database of Systematic Reviews",
          "year": 2020,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-time-lapse",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Time-Lapse Embryo Monitoring",
      "icon": "📹",
      "body": {
        "name": "Time-Lapse Embryo Monitoring (EmbryoScope)",
        "category": "clinical",
        "description": "Continuous camera monitoring of embryo development inside the incubator, allowing embryologists to assess embryo quality without disturbing the culture environment.",
        "howItWorks": "A camera inside the incubator takes images every 10-20 minutes, creating a time-lapse video of embryo development. AI algorithms can analyze morphokinetic parameters (timing of cell divisions) to help select the embryo most likely to implant. The embryos are never removed from the incubator for assessment.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "costRange": {
          "low": 500,
          "high": 1500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Undisturbed culture environment - embryos stay in optimal conditions",
            "More data points for embryo selection than traditional assessment",
            "Fascinating time-lapse video of your embryo developing"
          ],
          "shouldNotExpect": [
            "The evidence for improved live birth rates is mixed - selection may not be better than traditional grading",
            "Time-lapse does not replace PGT-A genetic testing for embryo selection",
            "The higher cost comes with uncertain added clinical benefit"
          ]
        },
        "considerations": [
          "Many modern clinics use time-lapse incubators as standard equipment",
          "Ask if the charge is for the monitoring or just use of the incubator",
          "The undisturbed culture benefit may be more important than the selection algorithm"
        ],
        "bestFor": [
          "Embryo selection",
          "Multiple embryos to choose from",
          "Patients wanting detailed development data"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "The Cochrane review found insufficient evidence that time-lapse imaging improves live birth, clinical pregnancy, miscarriage, or stillbirth rates compared with conventional embryo assessment",
            "mechanism": "Algorithms that track minute-by-minute embryo development add data points, but experienced embryologists achieve similar selection outcomes with standard daily observation",
            "source": "Armstrong et al., Cochrane Database of Systematic Reviews 2019 (9 RCTs, n=2,955)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC6539473/"
          },
          {
            "direction": "neutral",
            "outcome": "The SelecTIMO RCT found that neither time-lapse-based selection (EEVA algorithm) nor uninterrupted culture improved cumulative ongoing pregnancy compared with routine methods",
            "magnitude": "12-month ongoing pregnancy: 50.8% TLE vs 50.9% TL routine vs 49.4% control (p=0.85)",
            "source": "Kieslinger et al., The Lancet 2023 (SelecTIMO RCT, n=1,731)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/37004670/"
          },
          {
            "direction": "neutral",
            "outcome": "The TILT trial, the largest international RCT of time-lapse imaging, found no significant difference in live birth between time-lapse selection, undisturbed culture, and standard care",
            "magnitude": "Live birth 33.7% time-lapse vs 36.6% undisturbed culture vs 33.0% standard care",
            "source": "Bhide et al., The Lancet 2024 (TILT RCT, n=1,575)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/39033010/"
          }
        ],
        "headlineStat": "No proven birth benefit"
      },
      "bodyFr": {
        "name": "Monitoring time-lapse (EmbryoScope, GERI)",
        "category": "clinical",
        "description": "Caméra qui suit en continu le développement de l'embryon dans l'incubateur, permettant aux embryologistes d'évaluer sa qualité sans perturber l'environnement de culture. En France, le time-lapse (EmbryoScope, GERI…) est disponible dans un nombre croissant de centres d'AMP modernes, en public comme en privé. L'acte reste classé hors nomenclature par l'Assurance Maladie : quand il est utilisé, un supplément forfaitaire est demandé au·à la patient·e (souvent autour de 150 à 400 € selon le laboratoire).",
        "howItWorks": "Une caméra à l'intérieur de l'incubateur prend une image toutes les 10 à 20 minutes, créant une vidéo time-lapse du développement embryonnaire. Des algorithmes morphocinétiques (timing des divisions cellulaires) peuvent aider à sélectionner l'embryon le plus susceptible de s'implanter. Les embryons ne sont jamais sortis de l'incubateur pour l'évaluation, ce qui réduit les variations de température et de pH.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "costRange": {
          "low": 150,
          "high": 400
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Disponible dans de nombreux centres d'AMP français modernes (CHU, cliniques privées)",
            "Environnement de culture non perturbé : l'embryon reste dans des conditions optimales",
            "Plus de données pour la sélection embryonnaire qu'avec l'évaluation traditionnelle",
            "Une vidéo time-lapse du développement de ton embryon dans certains centres"
          ],
          "shouldNotExpect": [
            "Non remboursé par la Sécurité sociale (hors nomenclature) : un supplément forfaitaire est demandé quand il est utilisé",
            "Les preuves d'amélioration des taux de naissance vivante restent mitigées : la sélection n'est pas forcément meilleure qu'avec l'évaluation classique",
            "Le time-lapse ne remplace pas un test génétique embryonnaire pour la sélection"
          ]
        },
        "considerations": [
          "Statut en France : disponible dans beaucoup de centres mais classé HN, donc supplément à ta charge, non couvert par la Sécurité sociale (certaines mutuelles prennent en charge les actes HN)",
          "Demande à ton centre si l'incubateur time-lapse est utilisé en routine ou seulement pour certaines indications",
          "Le bénéfice de la culture non perturbée est peut-être plus important que l'algorithme de sélection lui-même",
          "Renseigne-toi auprès de ta mutuelle : certaines remboursent une partie des actes hors nomenclature en AMP"
        ],
        "bestFor": [
          "Sélection embryonnaire",
          "Plusieurs embryons à choisir",
          "Patient·es voulant des données détaillées sur le développement"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "La revue Cochrane n'a pas trouvé de preuves suffisantes que l'imagerie time-lapse améliore la naissance vivante, la grossesse clinique, les fausses couches ou les morts in utero par rapport à l'évaluation conventionnelle",
            "mechanism": "Les algorithmes qui suivent le développement embryonnaire minute par minute ajoutent des données, mais des embryologistes expérimenté·es obtiennent des résultats de sélection similaires avec l'observation quotidienne standard",
            "source": "Armstrong et al., Cochrane Database of Systematic Reviews 2019 (9 RCTs, n=2,955)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC6539473/"
          },
          {
            "direction": "neutral",
            "outcome": "L'essai SelecTIMO a trouvé que ni la sélection par time-lapse (algorithme EEVA) ni la culture non perturbée n'amélioraient la grossesse évolutive cumulée vs méthodes de routine",
            "magnitude": "Grossesse évolutive à 12 mois : 50,8 % TLE vs 50,9 % TL routine vs 49,4 % contrôle (p=0,85)",
            "source": "Kieslinger et al., The Lancet 2023 (SelecTIMO RCT, n=1,731)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/37004670/"
          },
          {
            "direction": "neutral",
            "outcome": "L'essai TILT, le plus grand essai international randomisé sur l'imagerie time-lapse, n'a pas montré de différence significative de naissance vivante entre time-lapse avec sélection, culture non perturbée et soins standard",
            "magnitude": "Naissance vivante 33,7 % time-lapse vs 36,6 % culture non perturbée vs 33,0 % soins standard",
            "source": "Bhide et al., The Lancet 2024 (TILT RCT, n=1,575)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/39033010/"
          }
        ],
        "headlineStat": "Pas de bénéfice prouvé sur la naissance"
      },
      "sources": [
        {
          "title": "Time-lapse imaging for embryo selection: a Cochrane systematic review",
          "journal": "Cochrane Database of Systematic Reviews",
          "year": 2019,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-calcium-ionophore",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Calcium Ionophore Egg Activation",
      "icon": "⚗️",
      "body": {
        "name": "Calcium Ionophore Egg Activation",
        "category": "clinical",
        "description": "A lab technique used after ICSI where eggs are briefly exposed to a calcium ionophore solution to trigger activation when fertilization has failed or been very low in previous cycles.",
        "howItWorks": "Normally, sperm entry triggers calcium signals inside the egg that start embryo development. In artificial oocyte activation, the embryology lab uses calcium ionophore after ICSI to mimic that activation signal. It is mainly considered after total or very low fertilization in prior ICSI cycles, not as a routine add-on.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "costRange": {
          "low": 500,
          "high": 1500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Performed in the lab after ICSI - no extra procedure for you",
            "Most relevant after previous total fertilization failure or very low fertilization in past cycles",
            "May improve fertilization and live birth outcomes in carefully selected cases"
          ],
          "shouldNotExpect": [
            "This should not be added routinely to every ICSI cycle",
            "Safety data on child development and birth outcomes are still limited",
            "It does not solve embryo quality problems unrelated to failed egg activation"
          ]
        },
        "considerations": [
          "Ask whether your clinic has a clear indication, such as prior failed or low fertilization after ICSI",
          "Ask which protocol they use and what safety follow-up data they can share",
          "This is best discussed with the embryology lab, not only the treating physician"
        ],
        "bestFor": [
          "Prior failed fertilization after ICSI",
          "Very low fertilization rate",
          "Suspected egg activation failure"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Meta-analysis of 22 studies found that calcium ionophore activation with ICSI improved clinical pregnancy and live birth rates compared with conventional ICSI, particularly in patients with previous failed or low fertilization",
            "magnitude": "Clinical pregnancy ~2× more likely (odds ratio 2.14, 95% CI 1.38-3.31); live birth ~2.6× more likely (odds ratio 2.65, 95% CI 1.53-4.60)",
            "mechanism": "Calcium ionophore mimics the calcium signal that normally triggers oocyte activation after sperm entry",
            "source": "Shan et al., Frontiers in Physiology 2022 (meta-analysis, 22 studies)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC8819094/"
          },
          {
            "direction": "positive",
            "outcome": "A 10-year updated meta-analysis found assisted oocyte activation with calcium ionophore improved overall fertilization rate and live birth, with the largest benefit in patients with previous fertilization failure",
            "magnitude": "Fertilization OR 1.99 (95% CI 1.16-3.41); live birth OR 4.58 (95% CI 1.52-13.80)",
            "source": "Zhang et al., Journal of Assisted Reproduction and Genetics 2025 (meta-analysis, 12 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/39557784/"
          },
          {
            "direction": "negative",
            "outcome": "In October 2023, the UK regulator (HFEA) removed artificial egg activation from its rated list of add-ons because it should only be offered in specific circumstances (e.g. PLC-zeta deficiency, prior fertilization failure), not as a routine ICSI add-on",
            "source": "HFEA treatment add-ons guidance 2023",
            "sourceUrl": "https://www.hfea.gov.uk/treatments/treatment-add-ons/"
          }
        ],
        "headlineStat": "Benefit only in failed fertilization"
      },
      "bodyFr": {
        "name": "Activation ovocytaire assistée à l'ionophore de calcium",
        "category": "clinical",
        "description": "Technique de laboratoire utilisée après ICSI : les ovocytes sont brièvement exposés à une solution d'ionophore de calcium (A23187) pour déclencher leur activation quand la fécondation a échoué ou a été très faible lors des cycles précédents. En France, cette technique n'est pas utilisée en routine ; elle est réservée à des indications précises (échec total ou quasi total de fécondation après ICSI antérieure, suspicion de déficit en PLC-zêta côté spermatozoïde, infertilité masculine sévère avec globozoospermie). Quelques laboratoires d'AMP français la pratiquent ; le HFEA britannique l'a retirée de sa liste d'add-ons notés en 2023 précisément pour réserver son usage à ces cas ciblés.",
        "howItWorks": "Normalement, l'entrée du spermatozoïde déclenche des signaux calciques dans l'ovocyte qui amorcent le développement embryonnaire. En activation ovocytaire assistée (AOA), le laboratoire d'embryologie expose brièvement les ovocytes à l'ionophore de calcium après l'ICSI pour imiter ce signal. Aucune étape supplémentaire pour toi : la décision se prend entre clinicien·ne et embryologiste, après analyse des cycles précédents.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "costRange": {
          "low": 0,
          "high": 500
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Réalisée en laboratoire après l'ICSI : aucune procédure supplémentaire pour toi",
            "En France : utilisée uniquement pour des indications précises (échec ou quasi-échec de fécondation lors d'une ICSI antérieure, globozoospermie, suspicion de déficit en PLC-zêta), pas en routine",
            "Quand elle est pratiquée dans le cadre d'une indication AMP validée, elle peut être intégrée au parcours pris en charge, à vérifier avec ton laboratoire",
            "Peut améliorer significativement la fécondation et la naissance vivante dans ces cas sélectionnés"
          ],
          "shouldNotExpect": [
            "Pas un add-on à proposer en routine à tous les ICSI",
            "Les données de sécurité à long terme sur le développement de l'enfant restent limitées",
            "Ne résout pas les problèmes de qualité ovocytaire ou embryonnaire qui ne sont pas liés à un défaut d'activation",
            "Le HFEA (régulateur britannique) l'a retirée de sa liste d'add-ons en 2023 précisément pour réserver son usage à des indications ciblées"
          ]
        },
        "considerations": [
          "Statut en France : pratiquée dans quelques laboratoires d'AMP, sur indication précise, pas un acte de routine ni un produit commercial proposé à tou·tes",
          "Demande si l'indication est claire dans ton dossier (antécédent d'échec/faible fécondation en ICSI, infertilité masculine sévère type globozoospermie)",
          "Discute avec l'embryologiste autant qu'avec le clinicien·ne : c'est une décision de laboratoire",
          "Renseigne-toi sur la facturation : selon l'indication et le centre, l'AOA peut être intégrée à l'AMP ou facturée à part"
        ],
        "bestFor": [
          "Antécédent d'échec total ou quasi total de fécondation après ICSI",
          "Taux de fécondation très faible en ICSI antérieure",
          "Infertilité masculine sévère (globozoospermie, suspicion de déficit en PLC-zêta)",
          "Pas pour une ICSI standard sans antécédent"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Une méta-analyse de 22 études a montré que l'activation à l'ionophore de calcium combinée à l'ICSI améliore les taux de grossesse clinique et de naissance vivante par rapport à l'ICSI conventionnelle, surtout en cas d'antécédent d'échec ou de faible fécondation",
            "magnitude": "Grossesse clinique ~2× plus probable (rapport de cotes 2,14, IC 95 % 1,38-3,31) ; naissance vivante ~2,6× plus probable (rapport de cotes 2,65, IC 95 % 1,53-4,60)",
            "mechanism": "L'ionophore de calcium imite le signal calcique qui déclenche normalement l'activation de l'ovocyte après l'entrée du spermatozoïde",
            "source": "Shan et al., Frontiers in Physiology 2022 (meta-analysis, 22 studies)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC8819094/"
          },
          {
            "direction": "positive",
            "outcome": "Une méta-analyse actualisée sur 10 ans a montré que l'activation ovocytaire à l'ionophore de calcium améliore le taux de fécondation et la naissance vivante, le bénéfice le plus net étant chez les patient·es avec un antécédent d'échec de fécondation",
            "magnitude": "Fécondation OR 1,99 (IC 95 % 1,16-3,41) ; naissance vivante OR 4,58 (IC 95 % 1,52-13,80)",
            "source": "Zhang et al., Journal of Assisted Reproduction and Genetics 2025 (meta-analysis, 12 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/39557784/"
          },
          {
            "direction": "negative",
            "outcome": "En octobre 2023, le régulateur britannique (HFEA) a retiré l'activation ovocytaire artificielle de sa liste d'add-ons notés car elle ne doit être proposée que dans des situations précises (déficit en PLC-zêta, échec de fécondation antérieur), et pas en add-on de routine en ICSI",
            "source": "HFEA treatment add-ons guidance 2023",
            "sourceUrl": "https://www.hfea.gov.uk/treatments/treatment-add-ons/"
          }
        ],
        "headlineStat": "Réservée à des indications précises"
      },
      "sources": [
        {
          "title": "Artificial oocyte activation with calcium ionophore in assisted reproduction",
          "journal": "Human Reproduction Update",
          "year": 2021,
          "evidenceLevel": "meta-analysis"
        },
        {
          "title": "Best practice guidelines for artificial oocyte activation using calcium ionophore",
          "journal": "Human Fertility",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-advanced-sperm-selection",
      "kind": "therapy",
      "tags": ["his", "during-cycle"],
      "title": "Advanced Sperm Selection",
      "icon": "🔬",
      "body": {
        "name": "Advanced Sperm Selection",
        "category": "clinical",
        "description": "Lab add-ons such as PICSI, IMSI, MACS, and microfluidic sperm sorting that try to select sperm with better maturity, morphology, motility, or DNA integrity before ICSI.",
        "howItWorks": "Each method uses different selection rules: PICSI picks sperm that bind to hyaluronan (a sign of maturity), IMSI uses very high magnification, MACS uses magnets to remove damaged sperm, and microfluidic devices separate motile sperm through tiny channels. The goal is better sperm selection, but the evidence depends heavily on the method and patient subgroup.",
        "evidenceStrength": "mixed",
        "forWhom": "him",
        "costRange": {
          "low": 300,
          "high": 1500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A lab-only add-on during sperm preparation or ICSI",
            "May improve sperm sample characteristics before injection",
            "Microfluidic sorting has more promising early evidence than some older methods"
          ],
          "shouldNotExpect": [
            "PICSI, IMSI, and MACS have not reliably improved live birth rates in routine use",
            "Better-looking sperm metrics do not always translate into better treatment outcomes",
            "The right question is which method, for which sperm issue, and at what cost"
          ]
        },
        "considerations": [
          "Ask which exact selection method your clinic is recommending",
          "Ask whether the indication is high DNA fragmentation, prior fertilization failure, or severe male factor",
          "Avoid paying for a bundled 'advanced sperm selection' add-on without knowing the technique"
        ],
        "bestFor": [
          "Male factor infertility",
          "High sperm DNA fragmentation",
          "Prior poor fertilization or embryo development"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "PICSI, IMSI, and MACS are not recommended for routine clinical use by ESHRE",
            "mechanism": "Studies have not shown consistent improvement in live birth compared with standard preparation or ICSI",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "positive",
            "outcome": "Microfluidic sperm selection can be considered, but evidence is still developing",
            "mechanism": "Microfluidic channels may enrich motile sperm with better morphology and DNA integrity",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "neutral",
            "outcome": "HFEA lists IMSI and PICSI as treatment add-ons with limited evidence for improving the chance of having a baby",
            "source": "HFEA treatment add-ons guidance 2026"
          }
        ],
        "headlineStat": "Method-dependent"
      },
      "bodyFr": {
        "name": "Sélection spermatique avancée",
        "category": "clinical",
        "description": "Add-ons de laboratoire (PICSI, IMSI, MACS, tri microfluidique) qui cherchent à sélectionner les spermatozoïdes avec une meilleure maturité, morphologie, mobilité ou intégrité de l'ADN avant l'ICSI.",
        "howItWorks": "Chaque méthode a son propre critère : le PICSI sélectionne les spermatozoïdes capables de se lier à l'acide hyaluronique (un signe de maturité), l'IMSI utilise un très fort grossissement, le MACS retire les spermatozoïdes endommagés par tri magnétique, et les dispositifs microfluidiques séparent les spermatozoïdes mobiles via de petits canaux. L'objectif est une meilleure sélection, mais le niveau de preuve dépend fortement de la méthode et du sous-groupe de patient·es.",
        "evidenceStrength": "mixed",
        "forWhom": "him",
        "costRange": {
          "low": 200,
          "high": 900
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Un add-on de laboratoire pendant la préparation du sperme ou l'ICSI",
            "Peut améliorer certaines caractéristiques du sperme avant injection",
            "Le tri microfluidique a des données plus prometteuses que certaines méthodes plus anciennes"
          ],
          "shouldNotExpect": [
            "PICSI, IMSI et MACS n'ont pas démontré d'amélioration fiable du taux de naissance vivante en usage de routine",
            "De meilleurs indicateurs spermatiques ne se traduisent pas toujours par de meilleurs résultats",
            "La vraie question est : quelle méthode, pour quel problème spermatique, à quel coût ?"
          ]
        },
        "considerations": [
          "Demande exactement quelle méthode de sélection la clinique recommande",
          "Demande si l'indication est une fragmentation élevée de l'ADN, un échec de fécondation précédent ou un facteur masculin sévère",
          "Évite de payer un pack « sélection spermatique avancée » sans savoir précisément quelle technique est utilisée"
        ],
        "bestFor": [
          "Infertilité masculine",
          "Forte fragmentation de l'ADN spermatique",
          "Antécédent de mauvaise fécondation ou développement embryonnaire"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "PICSI, IMSI et MACS ne sont pas recommandés en usage clinique de routine par l'ESHRE",
            "mechanism": "Les études ne montrent pas d'amélioration constante de la naissance vivante vs préparation standard ou ICSI",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "positive",
            "outcome": "La sélection spermatique microfluidique peut être envisagée, mais les preuves sont encore en construction",
            "mechanism": "Les canaux microfluidiques peuvent enrichir le sperme en spermatozoïdes mobiles avec une meilleure morphologie et intégrité d'ADN",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "neutral",
            "outcome": "La HFEA classe l'IMSI et le PICSI parmi les add-ons avec preuves limitées d'amélioration des chances de naissance",
            "source": "HFEA treatment add-ons guidance 2026"
          }
        ],
        "headlineStat": "Dépend de la méthode"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        },
        {
          "title": "Advanced sperm selection strategies as a treatment for infertile couples",
          "journal": "Journal of Clinical Medicine",
          "year": 2022,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-imsi",
      "kind": "therapy",
      "tags": ["his", "during-cycle"],
      "title": "IMSI",
      "subtitle": "High magnification sperm selection add-on",
      "icon": "🔬",
      "body": {
        "name": "Intracytoplasmic Morphologic Sperm Injection (IMSI)",
        "category": "clinical",
        "description": "A sperm selection add-on where the embryology lab uses very high magnification to choose sperm that look structurally normal before ICSI.",
        "howItWorks": "Standard ICSI already involves selecting a sperm under a microscope. IMSI increases the magnification so the embryologist can inspect fine details of the sperm head and exclude sperm with visible vacuoles or abnormal shape. The selected sperm is then injected into the egg in the same way as ICSI.",
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "costRange": {
          "low": 500,
          "high": 1500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A lab-only add-on during ICSI",
            "More detailed visual selection of sperm morphology",
            "No extra physical procedure for either partner"
          ],
          "shouldNotExpect": [
            "Reliable improvement in live birth for routine IVF or ICSI",
            "A fix for sperm DNA damage or egg quality problems",
            "Clear benefit unless the clinic can explain the specific sperm indication"
          ]
        },
        "considerations": [
          "Ask why IMSI is being recommended instead of standard ICSI",
          "Ask whether the indication is severe male factor, repeated ICSI failure, or prior poor embryo development",
          "Ask for the clinic's own outcome data before paying extra"
        ],
        "bestFor": [
          "Selected severe male factor cases",
          "Prior poor fertilization or embryo development",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "HFEA rates IMSI grey for improving the chance of having a baby for most fertility patients",
            "source": "HFEA treatment add-ons guidance 2026"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend IMSI for routine clinical use",
            "mechanism": "Available studies have not shown consistent live birth improvement compared with standard ICSI",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023"
          }
        ],
        "headlineStat": "HFEA grey"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        },
        {
          "title": "Treatment add-ons with limited evidence",
          "journal": "HFEA treatment add-ons guidance",
          "year": 2026,
          "url": "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-picsi",
      "kind": "therapy",
      "tags": ["his", "during-cycle"],
      "title": "PICSI",
      "subtitle": "Hyaluronan sperm binding add-on",
      "icon": "🧲",
      "body": {
        "name": "Physiological Intracytoplasmic Sperm Injection (PICSI)",
        "category": "clinical",
        "description": "A sperm selection add-on where sperm are exposed to hyaluronan, a substance found around the egg, and sperm that bind are selected for ICSI.",
        "howItWorks": "The embryology lab places prepared sperm on a hyaluronan-coated surface. Sperm that bind may be more mature, so the embryologist selects a bound sperm and injects it into the egg using ICSI. The test changes sperm selection, not the rest of fertilization.",
        "evidenceStrength": "debunked",
        "forWhom": "him",
        "costRange": {
          "low": 400,
          "high": 1200
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A lab-only add-on during ICSI",
            "Selection based on sperm binding to hyaluronan",
            "Possible discussion in male factor infertility"
          ],
          "shouldNotExpect": [
            "Better live birth rates for routine use",
            "A proven way to overcome poor egg quality",
            "A reason to skip a full male factor evaluation"
          ]
        },
        "considerations": [
          "Ask whether the clinic is using PICSI because of male factor infertility or simply as a routine package",
          "Ask whether standard semen analysis or sperm DNA fragmentation testing would change the plan",
          "Avoid paying for it as a default add-on without a clear indication"
        ],
        "bestFor": [
          "Selected male factor discussions",
          "ICSI cycles where the clinic has a specific rationale",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "HFEA rates PICSI black for most patients having ICSI for male factor infertility",
            "mechanism": "Moderate or high quality evidence has not shown improved chance of having a baby",
            "source": "HFEA treatment add-ons guidance 2026"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend PICSI for routine clinical use",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023"
          }
        ],
        "headlineStat": "HFEA black"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        },
        {
          "title": "Treatment add-ons with limited evidence",
          "journal": "HFEA treatment add-ons guidance",
          "year": 2026,
          "url": "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-prp-ovarian",
      "kind": "therapy",
      "tags": ["her", "pre-cycle"],
      "title": "Platelet-Rich Plasma (PRP)",
      "subtitle": "Ovarian and intrauterine PRP add-on",
      "icon": "💉",
      "body": {
        "name": "Platelet-Rich Plasma (PRP) Fertility Treatments",
        "category": "clinical",
        "description": "Fertility add-ons where platelet-rich plasma from your own blood is injected into the ovaries or infused into the uterus with the goal of improving ovarian response, lining thickness, implantation, or pregnancy outcomes.",
        "howItWorks": "A blood sample is drawn and spun in a centrifuge to concentrate platelets and growth factors. For ovarian PRP, the concentrate is injected into ovarian tissue under ultrasound guidance. For intrauterine PRP, it is placed into the womb cavity with the hope of improving the lining. The theories are biologically plausible, but clinical evidence has not shown reliable improvement in live birth.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "sessionDuration": "30-60 minutes",
        "costRange": {
          "low": 2000,
          "high": 5000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "You may see this offered at some fertility clinics, often at high cost",
            "Ovarian PRP may be marketed for poor ovarian response or low reserve",
            "Intrauterine PRP may be marketed for thin lining or implantation failure"
          ],
          "shouldNotExpect": [
            "Reliable improvement in egg quality, AMH, lining response, implantation, or live birth",
            "Insurance will not cover this - it is not an established treatment",
            "A major-society endorsed treatment outside research or highly selected circumstances"
          ]
        },
        "considerations": [
          "Ask whether the clinic is recommending intraovarian PRP, intrauterine PRP, or both",
          "Ask what exact outcome it is supposed to improve: follicle count, egg yield, lining thickness, implantation, miscarriage, or live birth",
          "Be cautious of clinics promoting PRP heavily without sharing outcome data or trial participation",
          "The cost ($2,000-$5,000 per session) is significant for an unproven treatment",
          "If a provider recommends this, ask whether it is part of a registered clinical trial"
        ],
        "bestFor": [
          "Research setting",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "The first double-blind RCT of intraovarian PRP in poor responders found no improvement in euploid blastocyst development or pregnancy. PRP modestly increased mature oocytes retrieved, but clinical pregnancy was actually lower in the PRP arm (27%) than control (60%)",
            "magnitude": "Mature oocytes 10.45 vs 8.91 (p=0.008); clinical pregnancy 27% PRP vs 60% control",
            "mechanism": "The hypothesized reactivation of dormant follicles by platelet growth factors was not supported by controlled data",
            "source": "Barrenetxea et al., Human Reproduction 2024 (double-blind RCT, n=60)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/38423539/"
          },
          {
            "direction": "neutral",
            "outcome": "A systematic review pooling 10 mostly uncontrolled trials reported pregnancy and live birth after PRP in poor responders, but with very low certainty: pooled live birth was only 16.6% and pooled clinical pregnancy 25.4%, with high heterogeneity and no placebo arm in most studies",
            "magnitude": "Pooled live birth 16.6% (95% CI 8.8-26.1); clinical pregnancy 25.4% (95% CI 13.1-39.9)",
            "source": "Wu et al., Platelets 2024 (systematic review and meta-analysis, 10 studies, n=876)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/38214306/"
          },
          {
            "direction": "negative",
            "outcome": "PRP for ovarian rejuvenation is not endorsed by ESHRE as a routine clinical add-on - evidence remains insufficient and authors recommend it only within research protocols",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "negative",
            "outcome": "HFEA rates PRP treatments red because evidence is limited and there may be safety concerns",
            "source": "HFEA treatment add-ons guidance 2026"
          }
        ],
        "headlineStat": "HFEA red"
      },
      "bodyFr": {
        "name": "Rajeunissement ovarien par PRP (plasma riche en plaquettes)",
        "category": "clinical",
        "description": "Procédure dans laquelle du plasma riche en plaquettes prélevé sur ton propre sang est injecté dans les ovaires. C'est une technique expérimentale qui ne fait pas partie de l'AMP reconnue en France et n'est pas remboursée par l'Assurance Maladie. Quelques cliniques privées en France la proposent (souvent en lien avec des groupes internationaux type IVI) ; beaucoup de patient·es y accèdent à l'étranger (Espagne, Grèce, Belgique). Malgré l'engouement initial, plusieurs études contrôlées n'ont pas démontré de bénéfice significatif sur les résultats de fertilité.",
        "howItWorks": "Un échantillon de sang est prélevé puis centrifugé pour concentrer les plaquettes et les facteurs de croissance. Ce PRP est ensuite injecté dans le cortex ovarien sous échoguidage. La théorie veut que les facteurs de croissance réactivent les follicules dormants, mais les essais contrôlés n'ont pas confirmé ce mécanisme en pratique. En France, l'acte n'est pas codé dans le parcours AMP standard : il est réalisé hors nomenclature, sur devis.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "sessionDuration": "30 à 60 minutes",
        "costRange": {
          "low": 1500,
          "high": 3500
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "En France : technique expérimentale, non remboursée par la Sécurité sociale, proposée uniquement en secteur privé ou à l'étranger",
            "Les premières petites études étaient prometteuses, mais les essais contrôlés plus larges n'ont pas reproduit ces résultats",
            "Coût à ta charge intégrale : compter 1 500 à 3 500 € par séance en France, parfois plus en clinique internationale"
          ],
          "shouldNotExpect": [
            "Aucune preuve fiable que le PRP améliore la qualité ovocytaire, l'AMH ou les taux de grossesse",
            "Non pris en charge par l'Assurance Maladie : ce n'est pas une technique d'AMP reconnue en France",
            "Aucune société savante majeure (ASRM, ESHRE, sociétés françaises) ne recommande cette procédure en routine"
          ]
        },
        "considerations": [
          "Statut en France : hors nomenclature AMP, expérimental, sans cadre de remboursement par la Sécurité sociale",
          "Plusieurs essais randomisés n'ont pas montré de bénéfice significatif par rapport au placebo",
          "Méfie-toi des cliniques (en France ou à l'étranger) qui promeuvent fortement le PRP sans partager leurs propres données de résultats",
          "Le coût (1 500 à 3 500 €) est important pour un traitement non prouvé et entièrement à ta charge",
          "Si un·e praticien·ne te le propose, demande si c'est dans le cadre d'un essai clinique enregistré ou d'une pratique hors AMM"
        ],
        "bestFor": [],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "Le premier essai randomisé en double aveugle de PRP intraovarien chez les mauvaises répondeuses n'a montré aucune amélioration des blastocystes euploïdes ni des taux de grossesse. Le PRP a légèrement augmenté le nombre d'ovocytes matures recueillis, mais le taux de grossesse clinique était plus bas avec PRP (27 %) que sans (60 %)",
            "magnitude": "Ovocytes matures 10,45 vs 8,91 (p=0,008) ; grossesse clinique 27 % PRP vs 60 % contrôle",
            "mechanism": "L'hypothèse d'une réactivation des follicules dormants par les facteurs de croissance plaquettaires n'a pas été confirmée en conditions contrôlées",
            "source": "Barrenetxea et al., Human Reproduction 2024 (double-blind RCT, n=60)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/38423539/"
          },
          {
            "direction": "neutral",
            "outcome": "Une revue systématique poolant 10 essais (majoritairement non contrôlés) rapporte des taux de grossesse et de naissance vivante après PRP chez les mauvaises répondeuses, mais avec une très faible certitude : naissance vivante poolée à 16,6 % seulement, grossesse clinique à 25,4 %, forte hétérogénéité et pas de bras placebo dans la plupart des études",
            "magnitude": "Naissance vivante poolée 16,6 % (IC 95 % 8,8-26,1) ; grossesse clinique 25,4 % (IC 95 % 13,1-39,9)",
            "source": "Wu et al., Platelets 2024 (systematic review and meta-analysis, 10 studies, n=876)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/38214306/"
          },
          {
            "direction": "negative",
            "outcome": "Le PRP pour le « rajeunissement ovarien » n'est pas recommandé par l'ESHRE en add-on clinique de routine : les preuves restent insuffisantes et les auteurs n'envisagent son usage que dans le cadre de protocoles de recherche",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Expérimental, non reconnu en France"
      },
      "sources": [
        {
          "title": "Intraovarian platelet-rich plasma administration for poor ovarian responders: a systematic review and meta-analysis",
          "journal": "Journal of Assisted Reproduction and Genetics",
          "year": 2023,
          "evidenceLevel": "meta-analysis"
        },
        {
          "title": "Effect of autologous platelet-rich plasma on poor ovarian response: a randomized controlled trial",
          "journal": "Human Reproduction",
          "year": 2024,
          "evidenceLevel": "rct"
        },
        {
          "title": "Treatment add-ons with limited evidence",
          "journal": "HFEA treatment add-ons guidance",
          "year": 2026,
          "url": "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-intralipid",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Intralipid Infusion",
      "icon": "💧",
      "body": {
        "name": "Intralipid Infusion",
        "category": "clinical",
        "description": "An intravenous infusion of a fat emulsion (soybean oil) given around the time of embryo transfer to modulate immune activity that may interfere with implantation.",
        "howItWorks": "Intralipids are thought to suppress overactive natural killer (NK) cells in the uterus. The theory is that elevated NK cell activity may attack the implanting embryo. The fat emulsion may reduce this immune response, creating a more receptive environment.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "sessionDuration": "1-2 hours (IV infusion)",
        "costRange": {
          "low": 300,
          "high": 800
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Simple IV infusion - similar to getting a drip",
            "Generally well-tolerated with minimal side effects",
            "Some clinics report improved outcomes in patients with elevated NK cells"
          ],
          "shouldNotExpect": [
            "The role of NK cells in implantation failure is still debated among experts",
            "Blood NK cell levels do not reliably predict what is happening in the uterus",
            "No large RCTs have proven benefit - the evidence remains largely observational",
            "Many reproductive immunology tests and treatments remain controversial in the field"
          ]
        },
        "considerations": [
          "Discuss with your RE whether immune testing is appropriate for your situation",
          "The ASRM has not endorsed routine use of intralipids for implantation failure",
          "Consider the cost-benefit ratio given the limited evidence"
        ],
        "bestFor": [
          "Recurrent implantation failure (controversial)",
          "Elevated NK cells (debated marker)"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "A meta-analysis of 5 RCTs in women with recurrent implantation failure found that intralipid infusion around embryo transfer was associated with higher clinical pregnancy and live birth rates, but evidence quality was rated only moderate due to risk of bias",
            "magnitude": "Clinical pregnancy RR 1.55 (95% CI 1.16-2.07); live birth RR 1.83 (95% CI 1.42-2.35)",
            "mechanism": "Intralipid is thought to dampen the activity of natural killer (NK) immune cells in the blood, though the link between blood NK activity and what happens in the uterus is disputed",
            "source": "Rimmer et al., Journal of Obstetrics and Gynaecology Research 2021 (meta-analysis, 5 RCTs, n=843)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33754451/"
          },
          {
            "direction": "positive",
            "outcome": "A larger meta-analysis pooling 5 RCTs in women with RIF or recurrent pregnancy loss found similar improvements, but the authors explicitly caution that blinding was poor and effect estimates may be inflated",
            "magnitude": "Clinical pregnancy RR 1.48; live birth RR 1.85; no significant change in miscarriage",
            "source": "Marchand et al., Journal of Clinical and Translational Research 2023 (meta-analysis, 5 RCTs, n=840)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC10411978/"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend immunological tests or treatments such as intralipid as a routine IVF add-on - peripheral blood immune measures don't reliably reflect the local uterine immune environment",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Possible benefit in RIF, low certainty"
      },
      "bodyFr": {
        "name": "Perfusion d'Intralipides",
        "category": "clinical",
        "description": "Perfusion intraveineuse d'une émulsion lipidique (huile de soja) administrée autour du transfert d'embryon pour moduler l'activité immunitaire qui pourrait gêner l'implantation.",
        "howItWorks": "On pense que les intralipides freinent l'activité des cellules NK (natural killer) hyperactives dans l'utérus. La théorie est qu'une activité NK élevée attaquerait l'embryon en cours d'implantation. L'émulsion lipidique réduirait cette réponse immunitaire et créerait un environnement plus accueillant.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "sessionDuration": "1 à 2 heures (perfusion IV)",
        "costRange": {
          "low": 250,
          "high": 600
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Perfusion intraveineuse simple, comparable à un goutte-à-goutte classique",
            "Généralement bien tolérée, avec peu d'effets secondaires",
            "Certaines cliniques rapportent de meilleurs résultats chez les patientes avec NK élevées"
          ],
          "shouldNotExpect": [
            "Le rôle des NK dans l'échec d'implantation reste discuté par les experts",
            "Le taux de NK sanguin ne reflète pas fiablement ce qui se passe dans l'utérus",
            "Aucun grand essai randomisé n'a prouvé de bénéfice : les données restent largement observationnelles",
            "Les tests et traitements d'immunologie reproductive restent globalement controversés"
          ]
        },
        "considerations": [
          "Discute avec ton·ta médecin pour savoir si un bilan immunitaire est pertinent dans ta situation",
          "L'ASRM ne soutient pas l'usage de routine des intralipides en cas d'échec d'implantation",
          "Évalue le rapport coût/bénéfice étant donné les preuves limitées"
        ],
        "bestFor": [
          "Échec d'implantation à répétition (controversé)",
          "NK élevées (marqueur débattu)"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Une méta-analyse de 5 essais randomisés chez des femmes en échec d'implantation à répétition a trouvé que la perfusion d'intralipides autour du transfert était associée à des taux de grossesse clinique et de naissance vivante plus élevés, mais la qualité des preuves n'est notée que modérée à cause du risque de biais",
            "magnitude": "Grossesse clinique RR 1,55 (IC 95 % 1,16-2,07) ; naissance vivante RR 1,83 (IC 95 % 1,42-2,35)",
            "mechanism": "On pense que l'intralipide atténue l'activité des cellules immunitaires NK (natural killer) dans le sang, mais le lien entre l'activité NK sanguine et ce qui se passe dans l'utérus reste contesté",
            "source": "Rimmer et al., Journal of Obstetrics and Gynaecology Research 2021 (meta-analysis, 5 RCTs, n=843)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33754451/"
          },
          {
            "direction": "positive",
            "outcome": "Une méta-analyse plus large groupant 5 essais en RIF ou fausses couches à répétition trouve des améliorations similaires, mais les auteurs alertent explicitement sur la mauvaise qualité du double aveugle et le risque que les effets soient surestimés",
            "magnitude": "Grossesse clinique RR 1,48 ; naissance vivante RR 1,85 ; pas d'effet significatif sur les fausses couches",
            "source": "Marchand et al., Journal of Clinical and Translational Research 2023 (meta-analysis, 5 RCTs, n=840)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC10411978/"
          },
          {
            "direction": "negative",
            "outcome": "L'ESHRE ne recommande pas les tests ou traitements immunitaires comme l'intralipide en add-on FIV de routine : les mesures immunitaires du sang périphérique ne reflètent pas fidèlement l'environnement immunitaire utérin local",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Possible bénéfice en RIF, faible certitude"
      },
      "sources": [
        {
          "title": "Intralipid supplementation in IVF: a systematic review and meta-analysis",
          "journal": "Journal of Reproductive Immunology",
          "year": 2021,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-gcsf-uterine-infusion",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "G-CSF Uterine Infusion",
      "icon": "💧",
      "body": {
        "name": "G-CSF Uterine Infusion",
        "category": "clinical",
        "description": "An infusion of G-CSF (a growth-factor protein normally involved in immune signalling) directly into the uterus, sometimes offered for thin lining or recurrent implantation failure.",
        "howItWorks": "G-CSF is placed into the uterus with the goal of improving endometrial growth, vascular remodelling, immune signalling, and embryo adhesion. The biological rationale is plausible, but studies are small and mixed, and safety conclusions are limited.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "sessionDuration": "5-15 minutes",
        "costRange": {
          "low": 500,
          "high": 1500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A brief catheter-based uterine infusion, often around retrieval or transfer timing",
            "Some small studies suggest possible benefit in thin endometrium or implantation failure",
            "Usually offered only after a specific lining or implantation concern"
          ],
          "shouldNotExpect": [
            "ESHRE does not recommend routine intrauterine G-CSF because evidence is inconclusive",
            "A thicker lining does not always translate into higher live birth",
            "Safety and ideal timing are not firmly established"
          ]
        },
        "considerations": [
          "Ask whether the indication is thin lining, recurrent implantation failure, or a clinic protocol",
          "Ask what alternative causes of thin lining have already been evaluated",
          "Be cautious if it is presented as a guaranteed implantation fix"
        ],
        "bestFor": [
          "Thin endometrium (investigational)",
          "Recurrent implantation failure (investigational)"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "Meta-analyses show mixed results, with some low-certainty signals of benefit and other analyses showing no clear live birth improvement",
            "mechanism": "G-CSF may affect endometrial growth, immune signalling, angiogenesis, and embryo adhesion",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE concludes evidence is inconclusive and intrauterine G-CSF is not recommended",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Inconclusive"
      },
      "bodyFr": {
        "name": "Perfusion utérine de G-CSF",
        "category": "clinical",
        "description": "Perfusion de G-CSF (une protéine de croissance normalement impliquée dans la signalisation immunitaire) directement dans l'utérus, parfois proposée en cas d'endomètre fin ou d'échecs d'implantation à répétition.",
        "howItWorks": "Le G-CSF est introduit dans l'utérus pour améliorer la croissance endométriale, le remodelage vasculaire, la signalisation immunitaire et l'adhésion embryonnaire. La logique biologique est plausible, mais les études sont petites, contradictoires, et les conclusions de sécurité restent limitées.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "sessionDuration": "5 à 15 minutes",
        "costRange": {
          "low": 350,
          "high": 900
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Une brève perfusion utérine par cathéter, souvent autour de la ponction ou du transfert",
            "Quelques petites études suggèrent un possible bénéfice en cas d'endomètre fin ou d'échec d'implantation",
            "Habituellement proposée seulement après un problème identifié de muqueuse ou d'implantation"
          ],
          "shouldNotExpect": [
            "L'ESHRE ne recommande pas le G-CSF intra-utérin en routine, les preuves étant non concluantes",
            "Un endomètre plus épais ne se traduit pas toujours par une meilleure naissance vivante",
            "La sécurité et le timing idéal ne sont pas solidement établis"
          ]
        },
        "considerations": [
          "Demande si l'indication est un endomètre fin, un échec d'implantation à répétition ou un protocole maison",
          "Demande quelles autres causes d'endomètre fin ont déjà été évaluées",
          "Méfie-toi si c'est présenté comme une solution miracle à l'implantation"
        ],
        "bestFor": [
          "Endomètre fin (à titre exploratoire)",
          "Échec d'implantation à répétition (à titre exploratoire)"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "Les méta-analyses montrent des résultats contradictoires, avec quelques signaux de bénéfice à faible certitude et d'autres analyses sans amélioration claire de la naissance vivante",
            "mechanism": "Le G-CSF peut agir sur la croissance endométriale, la signalisation immunitaire, l'angiogenèse et l'adhésion embryonnaire",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "negative",
            "outcome": "L'ESHRE conclut que les preuves sont non concluantes et que le G-CSF intra-utérin n'est pas recommandé",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Non concluant"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-reproductive-immunology",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Reproductive Immunology",
      "icon": "🛡️",
      "body": {
        "name": "Reproductive Immunology",
        "category": "clinical",
        "description": "Immune testing and treatments such as NK-cell panels, steroids, IVIG, TNF-alpha blockers, and related protocols sometimes offered for recurrent implantation failure or pregnancy loss.",
        "howItWorks": "These add-ons are based on the theory that abnormal immune signalling may interfere with implantation or early pregnancy. In practice, blood immune tests may not reflect the uterine environment, diagnostic thresholds are inconsistent, and treatments can carry meaningful side effects.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 500,
          "high": 5000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "May be discussed after repeated failed transfers or pregnancy losses",
            "Often includes immune blood tests and medication protocols around transfer"
          ],
          "shouldNotExpect": [
            "Routine NK-cell testing or immune treatment is not supported by major guideline bodies",
            "Steroids, IVIG, and biologic immune drugs can have real risks and costs",
            "This is different from treating a diagnosed autoimmune disease such as antiphospholipid syndrome"
          ]
        },
        "considerations": [
          "Separate diagnosed autoimmune disease treatment from speculative IVF immune add-ons",
          "Ask what specific diagnosis is being treated and what evidence supports the exact medication",
          "Ask about infection risk, pregnancy risks, and monitoring before using immune-suppressing medication"
        ],
        "bestFor": [
          "Diagnosed autoimmune disease under specialist care",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "ESHRE states there is no clear rationale for many reproductive immunology tests used in ART, and recommends against routine immune testing or treatment as an add-on",
            "mechanism": "Peripheral blood immune measures do not reliably represent the local uterine immune environment",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "negative",
            "outcome": "The ASRM Practice Committee opinion on recurrent pregnancy loss recommends against routine alloimmune testing - including CD16-NK cells (circulating and mucosal), embryotoxic factor, cytokine profiles, blocking antibodies, HLA typing, and anti-paternal leukocyte antibodies - citing low patient volumes, lack of standardization, and poor reproducibility in supporting studies",
            "source": "ASRM Practice Committee, Fertility and Sterility 2026 (Committee Opinion on Recurrent Pregnancy Loss)",
            "sourceUrl": "https://www.asrm.org/practice-guidance/practice-committee-documents/recurrent-pregnancy-loss-a-committee-opinion-2026/"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE specifically does not recommend glucocorticoids in ART - efficacy is inconsistent and there are potential pregnancy safety concerns",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "neutral",
            "outcome": "This guidance applies to speculative IVF immune add-ons. It does not apply to patients receiving immune treatment for a diagnosed medical condition (e.g. antiphospholipid syndrome) under specialist care",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Not supported"
      },
      "bodyFr": {
        "name": "Immunologie reproductive",
        "category": "clinical",
        "description": "Tests et traitements immunitaires (bilans NK, corticoïdes, IVIG, anti-TNF-alpha et protocoles associés) parfois proposés en cas d'échec d'implantation à répétition ou de fausses couches.",
        "howItWorks": "Ces add-ons reposent sur l'idée qu'une signalisation immunitaire anormale gênerait l'implantation ou le début de grossesse. En pratique, les tests immunitaires sanguins ne reflètent pas l'environnement utérin, les seuils diagnostiques varient, et les traitements peuvent avoir des effets indésirables réels.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 350,
          "high": 3500
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Peut être évoqué après des transferts ou des grossesses en échec répétés",
            "Inclut souvent des prises de sang immunitaires et des protocoles médicamenteux autour du transfert"
          ],
          "shouldNotExpect": [
            "Les bilans NK et traitements immunitaires de routine ne sont pas soutenus par les sociétés savantes",
            "Corticoïdes, IVIG et biothérapies immunitaires ont des risques et des coûts réels",
            "C'est différent de la prise en charge d'une maladie auto-immune diagnostiquée (ex. syndrome des antiphospholipides)"
          ]
        },
        "considerations": [
          "Sépare bien le traitement d'une maladie auto-immune diagnostiquée des add-ons immunitaires spéculatifs en FIV",
          "Demande quel diagnostic précis est traité et quelles preuves soutiennent le médicament proposé",
          "Demande quels sont les risques infectieux, les risques pour la grossesse et la surveillance avant d'utiliser un traitement immunosuppresseur"
        ],
        "bestFor": [
          "Maladie auto-immune diagnostiquée suivie par un·e spécialiste",
          "Non recommandé comme add-on FIV de routine"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "L'ESHRE souligne l'absence de logique claire pour de nombreux tests d'immunologie reproductive utilisés en AMP et déconseille les tests ou traitements immunitaires en routine comme add-on",
            "mechanism": "Les mesures immunitaires du sang périphérique ne reflètent pas fidèlement l'environnement immunitaire local de l'utérus",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "negative",
            "outcome": "Le Practice Committee de l'ASRM déconseille les tests alloimmuns en routine, y compris CD16-NK (circulantes et muqueuses), facteur embryotoxique, profils de cytokines, anticorps bloquants, typage HLA, anticorps anti-leucocytes paternels, citant la petite taille des études, le manque de standardisation, et la faible reproductibilité",
            "source": "ASRM Practice Committee, Fertility and Sterility 2026 (Committee Opinion on Recurrent Pregnancy Loss)",
            "sourceUrl": "https://www.asrm.org/practice-guidance/practice-committee-documents/recurrent-pregnancy-loss-a-committee-opinion-2026/"
          },
          {
            "direction": "negative",
            "outcome": "L'ESHRE déconseille spécifiquement les corticoïdes en AMP : efficacité inconstante et préoccupations potentielles pour la grossesse",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "neutral",
            "outcome": "Cette recommandation vise les add-ons immunitaires spéculatifs en FIV. Elle ne concerne pas les patient·es traité·es pour une maladie immunitaire diagnostiquée (ex. syndrome des antiphospholipides) suivie par un·e spécialiste",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Non recommandé"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-ivig",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "IVIG",
      "subtitle": "Intravenous immunoglobulin immune add-on",
      "icon": "🛡️",
      "body": {
        "name": "Intravenous Immunoglobulin (IVIG)",
        "category": "clinical",
        "description": "An immune treatment where pooled human antibodies are given by IV infusion around fertility treatment, usually marketed for recurrent implantation failure, pregnancy loss, or abnormal immune testing.",
        "howItWorks": "IVIG is thought to modulate immune signalling and natural killer cell activity. In fertility add-on protocols, it is usually given before or around embryo transfer. The problem is that immune test thresholds are inconsistent, blood immune markers may not reflect the uterus, and evidence has not shown reliable live birth benefit for routine IVF use.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "sessionDuration": "2-4 hours (IV infusion)",
        "costRange": {
          "low": 2000,
          "high": 8000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A specialist IV infusion, often repeated across a cycle",
            "High cost and more monitoring than simpler add-ons",
            "Discussion mostly in recurrent implantation failure or recurrent pregnancy loss settings"
          ],
          "shouldNotExpect": [
            "Proven improvement in live birth for routine IVF use",
            "A clear answer from NK-cell blood testing alone",
            "Low-risk treatment just because it is called immune support"
          ]
        },
        "considerations": [
          "Separate treatment for a diagnosed immune disorder from speculative IVF immune add-ons",
          "Ask what specific diagnosis is being treated and what evidence supports IVIG for that diagnosis",
          "Ask about infusion reactions, infection-screening safeguards, thrombosis risk, and monitoring",
          "Ask whether a lower-risk evidence-based alternative exists"
        ],
        "bestFor": [
          "Diagnosed immune disease under specialist care",
          "Research setting",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "HFEA rates IVIG red as a fertility add-on because evidence is poor and there may be safety concerns",
            "source": "HFEA treatment add-ons guidance 2026"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not support routine immune add-ons for ART outside clear medical indications",
            "mechanism": "Peripheral immune markers do not reliably diagnose a uterine implantation problem",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023"
          }
        ],
        "headlineStat": "HFEA red"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        },
        {
          "title": "Treatment add-ons with limited evidence",
          "journal": "HFEA treatment add-ons guidance",
          "year": 2026,
          "url": "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-glucocorticoids",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Steroids (Glucocorticoids)",
      "subtitle": "Immune suppression add-on around transfer",
      "icon": "💊",
      "body": {
        "name": "Steroids (Glucocorticoids)",
        "category": "clinical",
        "description": "Short courses of steroids such as prednisolone, prednisone, methylprednisolone, or dexamethasone are sometimes added around embryo transfer to suppress inflammation or immune activity.",
        "howItWorks": "Glucocorticoids reduce inflammatory immune signalling. Clinics may prescribe them around transfer when they suspect immune activation, recurrent implantation failure, or recurrent pregnancy loss. Evidence does not support routine use as an IVF add-on, and side effects matter even with short courses.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 20,
          "high": 200
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Usually oral tablets around transfer or early pregnancy",
            "May be appropriate for a separate diagnosed medical condition",
            "Different drugs and doses across clinics"
          ],
          "shouldNotExpect": [
            "Proven improvement in live birth for routine IVF use",
            "A risk-free add-on because the course is short",
            "A substitute for evaluating real autoimmune or endocrine disease"
          ]
        },
        "considerations": [
          "Ask whether the steroid is treating a diagnosed condition or being used as a speculative add-on",
          "Ask about blood glucose, blood pressure, sleep, mood, infection risk, and pregnancy safety",
          "Do not start or stop prescribed steroids without clinician guidance"
        ],
        "bestFor": [
          "Diagnosed medical indications",
          "Clinic-directed short courses only",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "HFEA rates steroids red as a fertility add-on because evidence is poor and there may be safety concerns",
            "source": "HFEA treatment add-ons guidance 2026"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend glucocorticoids in ART as routine add-ons",
            "mechanism": "Studies show inconsistent efficacy and steroids can create maternal and pregnancy safety issues",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023"
          }
        ],
        "headlineStat": "HFEA red"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        },
        {
          "title": "Treatment add-ons with limited evidence",
          "journal": "HFEA treatment add-ons guidance",
          "year": 2026,
          "url": "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-elective-freeze-all",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Elective Freeze-All",
      "icon": "❄️",
      "body": {
        "name": "Elective Freeze-All",
        "category": "clinical",
        "description": "A strategy where all embryos from an IVF cycle are frozen and transfer is delayed to a later frozen embryo transfer cycle, even when there is no urgent medical reason.",
        "howItWorks": "Freeze-all is medically useful when there is a risk of OHSS (ovarian hyperstimulation syndrome, an over-response to fertility drugs), when PGT results are pending, or when the lining and hormones are not suitable for fresh transfer. As an elective add-on for everyone, it adds freezing, thawing, cost, and delay without clear evidence of better cumulative live birth.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "costRange": {
          "low": 1000,
          "high": 4000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A valid strategy when there is OHSS risk, PGT-A testing, or a poor fresh-transfer environment",
            "More control over transfer timing",
            "A delay before transfer and additional freezing/thawing steps"
          ],
          "shouldNotExpect": [
            "Routine freeze-all for everyone has not shown superior cumulative live birth",
            "It can increase cost and time to pregnancy",
            "It may carry different obstetric risk tradeoffs compared with fresh transfer"
          ]
        },
        "considerations": [
          "Ask whether the reason is medical, lab/logistics, PGT-A, or clinic routine",
          "Ask how your fresh-transfer prognosis compares with a freeze-all strategy",
          "OHSS prevention is different from elective freeze-all as an add-on"
        ],
        "bestFor": [
          "OHSS prevention",
          "PGT-A cycles",
          "High progesterone or poor lining in stimulation cycle"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "ESHRE does not recommend elective freeze-all for routine clinical use",
            "mechanism": "Evidence does not show superior cumulative live birth over fresh transfer for all patients",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "positive",
            "outcome": "Freeze-all remains appropriate for specific indications such as OHSS prevention or genetic testing workflows",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Not routine"
      },
      "bodyFr": {
        "name": "Freeze-all électif",
        "category": "clinical",
        "description": "Stratégie consistant à congeler tous les embryons d'un cycle de FIV et à reporter le transfert à un cycle ultérieur (transfert d'embryon congelé), même sans raison médicale urgente.",
        "howItWorks": "Le freeze-all est médicalement utile en cas de risque d'OHSS (syndrome d'hyperstimulation ovarienne, une réaction excessive aux médicaments de fertilité), de PGT en attente, ou quand la muqueuse et les hormones ne sont pas favorables à un transfert frais. En revanche, en routine pour toutes les patientes, il ajoute congélation, décongélation, coût et délai, sans preuve claire d'un meilleur taux cumulé de naissance vivante.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "costRange": {
          "low": 700,
          "high": 2500
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Stratégie valable en cas de risque d'OHSS, de PGT-A, ou d'un mauvais environnement pour le transfert frais",
            "Un meilleur contrôle sur le moment du transfert",
            "Un délai supplémentaire avant le transfert et des étapes de congélation/décongélation en plus"
          ],
          "shouldNotExpect": [
            "Le freeze-all systématique pour tout le monde n'a pas montré de meilleur taux cumulé de naissance vivante",
            "Peut augmenter le coût et le délai jusqu'à la grossesse",
            "Peut comporter des compromis obstétricaux différents par rapport au transfert frais"
          ]
        },
        "considerations": [
          "Demande si la raison est médicale, liée au labo/à la logistique, au PGT-A, ou simplement un protocole maison",
          "Demande comment ton pronostic de transfert frais se compare à celui d'un freeze-all",
          "La prévention de l'OHSS est différente du freeze-all électif comme add-on"
        ],
        "bestFor": [
          "Prévention de l'OHSS",
          "Cycles avec PGT-A",
          "Progestérone élevée ou muqueuse de mauvaise qualité en cycle de stimulation"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "L'ESHRE ne recommande pas le freeze-all électif en usage clinique de routine",
            "mechanism": "Les preuves ne montrent pas de supériorité du freeze-all sur le transfert frais en termes de naissance vivante cumulée pour toutes les patientes",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "positive",
            "outcome": "Le freeze-all reste pertinent pour des indications précises comme la prévention de l'OHSS ou les flux de test génétique",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Pas en routine"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-intrauterine-culture",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Intrauterine Culture",
      "icon": "🧫",
      "body": {
        "name": "Intrauterine Culture Devices",
        "category": "clinical",
        "description": "Devices that place eggs and sperm or early embryos in a small capsule inside the vagina or uterus for early embryo culture instead of using a standard incubator.",
        "howItWorks": "The device is loaded in the lab and placed in the body for several days before embryos are removed and assessed. These systems are marketed as a more natural or participatory culture environment, but evidence has not shown better live birth outcomes compared with standard IVF culture.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 800,
          "high": 2500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A different embryo culture workflow from a standard incubator",
            "Some people may find the concept emotionally appealing"
          ],
          "shouldNotExpect": [
            "Better live birth rates have not been proven",
            "It is not standard care in most IVF labs",
            "It can add cost and logistical complexity"
          ]
        },
        "considerations": [
          "Ask why this is being recommended over conventional laboratory culture",
          "Ask whether the clinic has outcome data specific to this device"
        ],
        "bestFor": ["Not recommended as routine IVF add-on"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend intravaginal or intrauterine culture devices for routine clinical use",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "negative",
            "outcome": "HFEA has historically listed intrauterine culture among add-ons with limited evidence",
            "source": "HFEA treatment add-ons guidance"
          }
        ],
        "headlineStat": "Not supported"
      },
      "bodyFr": {
        "name": "Dispositifs de culture intra-utérine",
        "category": "clinical",
        "description": "Dispositifs qui placent ovocytes et spermatozoïdes ou embryons précoces dans une petite capsule à l'intérieur du vagin ou de l'utérus pour la culture embryonnaire initiale, à la place d'un incubateur de laboratoire.",
        "howItWorks": "Le dispositif est chargé au laboratoire puis placé dans le corps pendant plusieurs jours avant que les embryons soient récupérés et évalués. Ces systèmes sont présentés comme un environnement de culture plus naturel ou participatif, mais les données ne montrent pas de meilleur taux de naissance vivante vs culture standard en FIV.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 600,
          "high": 1800
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Un flux de culture embryonnaire différent de l'incubateur classique",
            "Ce concept peut être émotionnellement parlant pour certaines personnes"
          ],
          "shouldNotExpect": [
            "Aucune preuve d'un meilleur taux de naissance vivante",
            "Ce n'est pas la norme dans la plupart des laboratoires de FIV",
            "Ça ajoute du coût et de la complexité logistique"
          ]
        },
        "considerations": [
          "Demande pourquoi c'est recommandé plutôt qu'une culture en laboratoire classique",
          "Demande si la clinique a ses propres données de résultats sur ce dispositif"
        ],
        "bestFor": ["Non recommandé comme add-on FIV de routine"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "L'ESHRE ne recommande pas les dispositifs de culture intravaginale ou intra-utérine en usage clinique de routine",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          },
          {
            "direction": "negative",
            "outcome": "La HFEA a historiquement classé la culture intra-utérine parmi les add-ons à preuves limitées",
            "source": "HFEA treatment add-ons guidance"
          }
        ],
        "headlineStat": "Non recommandé"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-intrauterine-hcg",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Intrauterine hCG Infusion",
      "icon": "💧",
      "body": {
        "name": "Intrauterine hCG Infusion",
        "category": "clinical",
        "description": "A uterine infusion of hCG around embryo transfer, offered by some clinics with the goal of improving implantation signalling.",
        "howItWorks": "hCG is the hormone the early embryo releases to signal pregnancy. The theory is that placing hCG into the uterus before or around transfer may prime the lining for implantation. Studies are conflicting, with results varying by dose, timing, whether the embryo is transferred at day 3 (cleavage stage) or day 5 (blastocyst stage), and patient subgroup.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "sessionDuration": "5-15 minutes",
        "costRange": {
          "low": 200,
          "high": 800
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A brief catheter-based uterine infusion near transfer timing",
            "Some studies suggest improved outcomes in selected or recurrent-failure groups"
          ],
          "shouldNotExpect": [
            "The evidence is inconsistent and not strong enough for routine use",
            "It should not be presented as a guaranteed implantation treatment"
          ]
        },
        "considerations": [
          "Ask about dose, timing, and whether evidence applies to blastocyst transfer",
          "Ask whether the recommendation is for recurrent implantation failure or routine transfer"
        ],
        "bestFor": [
          "Recurrent implantation failure (debated)",
          "Research setting"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "ESHRE found conflicting evidence for intrauterine hCG and does not recommend it routinely",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Conflicting"
      },
      "bodyFr": {
        "name": "Perfusion intra-utérine d'hCG",
        "category": "clinical",
        "description": "Perfusion utérine d'hCG autour du transfert d'embryon, proposée par certaines cliniques pour améliorer la signalisation d'implantation.",
        "howItWorks": "L'hCG est l'hormone que libère l'embryon précoce pour signaler la grossesse. La théorie est qu'introduire de l'hCG dans l'utérus avant ou autour du transfert préparerait la muqueuse à l'implantation. Les études sont contradictoires : les résultats varient selon la dose, le moment, le stade de l'embryon (jour 3 / stade de clivage, ou jour 5 / blastocyste) et le sous-groupe de patientes.",
        "evidenceStrength": "mixed",
        "forWhom": "her",
        "sessionDuration": "5 à 15 minutes",
        "costRange": {
          "low": 150,
          "high": 500
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Une brève perfusion utérine par cathéter autour du transfert",
            "Quelques études suggèrent de meilleurs résultats chez des groupes sélectionnés ou en cas d'échecs répétés"
          ],
          "shouldNotExpect": [
            "Les preuves sont inconstantes et pas assez solides pour un usage de routine",
            "Ça ne doit pas être présenté comme un traitement garanti pour l'implantation"
          ]
        },
        "considerations": [
          "Demande la dose, le timing, et si les preuves s'appliquent au transfert de blastocyste",
          "Demande si la recommandation cible l'échec d'implantation à répétition ou un transfert de routine"
        ],
        "bestFor": [
          "Échec d'implantation à répétition (débattu)",
          "Cadre de recherche"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "L'ESHRE a trouvé des preuves contradictoires pour l'hCG intra-utérine et ne la recommande pas en routine",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Contradictoire"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-endometrial-priming-addons",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Endometrial Priming",
      "icon": "🌙",
      "body": {
        "name": "Endometrial Priming",
        "category": "clinical",
        "description": "Experimental approaches that expose the uterus to embryo culture fluid, seminal plasma, or similar biologic signals before transfer to try to improve implantation.",
        "howItWorks": "These add-ons are based on the idea that uterine exposure to embryo-derived or seminal factors could improve receptivity. Protocols vary widely, and evidence is very low certainty for live birth benefit.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 200,
          "high": 1000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A transfer-cycle add-on some clinics may describe as uterine priming",
            "Usually brief and procedural rather than a medication course"
          ],
          "shouldNotExpect": [
            "Reliable improvement in live birth has not been shown",
            "The exact product, timing, and technique matter and are not standardized"
          ]
        },
        "considerations": [
          "Ask exactly what is being placed in the uterus or vagina",
          "Ask whether it is embryo culture supernatant, seminal plasma, or another protocol",
          "Avoid paying for vague 'uterine priming' without named evidence"
        ],
        "bestFor": ["Not recommended as routine IVF add-on"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend endometrial administration of embryo culture supernatant or seminal plasma exposure",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Not supported"
      },
      "bodyFr": {
        "name": "Priming endométrial",
        "category": "clinical",
        "description": "Approches expérimentales qui exposent l'utérus au liquide de culture embryonnaire, au plasma séminal ou à d'autres signaux biologiques avant le transfert pour tenter d'améliorer l'implantation.",
        "howItWorks": "Ces add-ons reposent sur l'idée qu'une exposition utérine à des facteurs dérivés de l'embryon ou du sperme améliorerait la réceptivité. Les protocoles varient beaucoup, et les preuves sont de très faible certitude pour un bénéfice sur la naissance vivante.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 150,
          "high": 700
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Un add-on du cycle de transfert, parfois présenté comme « priming utérin »",
            "Généralement bref et procédural plutôt qu'un traitement médicamenteux long"
          ],
          "shouldNotExpect": [
            "Aucune amélioration fiable de la naissance vivante démontrée",
            "Le produit, le timing et la technique précis comptent, et ne sont pas standardisés"
          ]
        },
        "considerations": [
          "Demande exactement ce qui est introduit dans l'utérus ou le vagin",
          "Demande si c'est du surnageant de culture embryonnaire, du plasma séminal ou un autre protocole",
          "Évite de payer un vague « priming utérin » sans preuves nommées"
        ],
        "bestFor": ["Non recommandé comme add-on FIV de routine"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "L'ESHRE ne recommande pas l'administration endométriale de surnageant de culture embryonnaire ni l'exposition au plasma séminal",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Non recommandé"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-growth-factor-culture-medium",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "Growth-Factor Culture Medium",
      "icon": "🧪",
      "body": {
        "name": "Growth-Factor Culture Medium",
        "category": "clinical",
        "description": "Special embryo culture media supplemented with growth factors, marketed to improve embryo development or implantation.",
        "howItWorks": "Embryos are cultured in media containing additional growth factors rather than standard culture media. The theory is biologically plausible, but clinical evidence has not shown reliable improvement in live birth or miscarriage outcomes.",
        "evidenceStrength": "debunked",
        "forWhom": "both",
        "costRange": {
          "low": 200,
          "high": 1000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A lab-only add-on with no extra patient procedure",
            "May sound similar to EmbryoGlue, but it is a different intervention"
          ],
          "shouldNotExpect": [
            "Proven improvement in live birth has not been shown",
            "A more complex culture medium is not automatically better"
          ]
        },
        "considerations": [
          "Ask whether your clinic means culture medium or transfer medium",
          "EmbryoGlue/hyaluronan transfer medium has a different evidence profile"
        ],
        "bestFor": ["Not recommended as routine IVF add-on"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "ESHRE found no evidence of benefit on live birth or miscarriage for growth-factor supplemented embryo culture medium",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "No benefit"
      },
      "bodyFr": {
        "name": "Milieu de culture aux facteurs de croissance",
        "category": "clinical",
        "description": "Milieux de culture embryonnaire spéciaux enrichis en facteurs de croissance, présentés comme améliorant le développement embryonnaire ou l'implantation.",
        "howItWorks": "Les embryons sont cultivés dans un milieu contenant des facteurs de croissance supplémentaires plutôt que dans un milieu standard. La théorie est biologiquement plausible, mais les preuves cliniques ne montrent pas d'amélioration fiable des taux de naissance vivante ou de fausse couche.",
        "evidenceStrength": "debunked",
        "forWhom": "both",
        "costRange": {
          "low": 150,
          "high": 700
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Un add-on de laboratoire, sans procédure supplémentaire pour la patiente",
            "Peut ressembler à l'EmbryoGlue, mais c'est une intervention différente"
          ],
          "shouldNotExpect": [
            "Aucune amélioration prouvée de la naissance vivante",
            "Un milieu plus complexe n'est pas automatiquement meilleur"
          ]
        },
        "considerations": [
          "Demande si la clinique parle de milieu de culture ou de milieu de transfert",
          "L'EmbryoGlue / milieu de transfert au hyaluronane a un profil de preuve différent"
        ],
        "bestFor": ["Non recommandé comme add-on FIV de routine"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "L'ESHRE n'a trouvé aucune preuve de bénéfice sur la naissance vivante ou la fausse couche pour le milieu de culture enrichi en facteurs de croissance",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Pas de bénéfice"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-icsi-non-male-factor",
      "kind": "therapy",
      "tags": ["her", "his", "during-cycle"],
      "title": "ICSI for Non-Male Factor",
      "icon": "🪡",
      "body": {
        "name": "ICSI for Non-Male Factor",
        "category": "clinical",
        "description": "Using ICSI instead of conventional IVF when there is no clear male-factor indication, often offered to reduce perceived fertilization risk.",
        "howItWorks": "With ICSI, an embryologist injects a single sperm into each mature egg. It is standard for many male-factor situations and some specific lab indications, but its routine use in non-male-factor cycles has not consistently improved live birth.",
        "evidenceStrength": "mixed",
        "forWhom": "both",
        "costRange": {
          "low": 1000,
          "high": 2500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Direct sperm injection for each mature egg",
            "Lower risk of total fertilization failure in some specific scenarios"
          ],
          "shouldNotExpect": [
            "Higher live birth for every patient without male factor",
            "A guarantee of better embryo quality"
          ]
        },
        "considerations": [
          "Ask whether ICSI is recommended because of sperm parameters, PGT workflow, prior fertilization history, or clinic routine",
          "Ask whether a split IVF/ICSI cycle is an option (half the eggs with conventional IVF, half with ICSI) when fertilization risk is uncertain"
        ],
        "bestFor": [
          "Male factor infertility",
          "Prior fertilization failure",
          "Specific lab or genetic-testing indications"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "Evidence for routine ICSI in non-male-factor infertility is conflicting and often does not show better live birth",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023; ASRM Committee Opinion 2026"
          }
        ],
        "headlineStat": "Not for all"
      },
      "bodyFr": {
        "name": "ICSI hors facteur masculin",
        "category": "clinical",
        "description": "Utilisation de l'ICSI à la place d'une FIV conventionnelle alors qu'il n'y a pas d'indication claire de facteur masculin, souvent proposée pour réduire un risque de fécondation perçu.",
        "howItWorks": "En ICSI, l'embryologiste injecte un spermatozoïde unique dans chaque ovocyte mature. C'est le standard pour de nombreuses situations de facteur masculin et certaines indications de laboratoire, mais son usage de routine dans les cycles hors facteur masculin n'a pas montré une meilleure naissance vivante.",
        "evidenceStrength": "mixed",
        "forWhom": "both",
        "costRange": {
          "low": 700,
          "high": 1800
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Une injection directe de spermatozoïde dans chaque ovocyte mature",
            "Un risque plus faible d'échec total de fécondation dans certains scénarios précis"
          ],
          "shouldNotExpect": [
            "Une meilleure naissance vivante pour toutes les patientes sans facteur masculin",
            "Une garantie d'une meilleure qualité embryonnaire"
          ]
        },
        "considerations": [
          "Demande si l'ICSI est recommandée à cause des paramètres spermatiques, du PGT, d'un antécédent d'échec de fécondation, ou simplement par habitude de la clinique",
          "Demande si un partage FIV/ICSI (split) est possible (la moitié des ovocytes en FIV classique, l'autre en ICSI) quand le risque de fécondation est incertain"
        ],
        "bestFor": [
          "Infertilité masculine",
          "Antécédent d'échec de fécondation",
          "Indications spécifiques de laboratoire ou de test génétique"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "Les preuves pour l'ICSI de routine en cas d'infertilité hors facteur masculin sont contradictoires et ne montrent souvent pas de meilleure naissance vivante",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023; ASRM Committee Opinion 2026"
          }
        ],
        "headlineStat": "Pas pour tout le monde"
      },
      "sources": [
        {
          "title": "Intracytoplasmic sperm injection for nonmale factor indications",
          "journal": "Fertility and Sterility",
          "year": 2026,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-androgen-supplementation",
      "kind": "therapy",
      "tags": ["her", "pre-cycle", "during-cycle"],
      "title": "Androgen Supplementation",
      "subtitle": "DHEA or testosterone before stimulation",
      "icon": "🧪",
      "body": {
        "name": "Androgen Supplementation",
        "category": "clinical",
        "description": "An IVF add-on where androgen precursors or hormones, commonly DHEA or testosterone, are used before or during ovarian stimulation with the goal of improving egg yield or response.",
        "howItWorks": "Androgens are involved in early follicle development and may affect how follicles respond to FSH. Some clinics use DHEA capsules, testosterone gel, or testosterone patches before stimulation in people with poor ovarian response. The theory is plausible, but evidence is not strong enough to support routine use for most patients.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "costRange": {
          "low": 30,
          "high": 500
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A pre-cycle medication or supplement plan, often started weeks before stimulation",
            "Hormone monitoring, especially testosterone and DHEA-S",
            "Clinic-specific protocols with variable dose and timing"
          ],
          "shouldNotExpect": [
            "A guaranteed increase in egg number, embryo number, or live birth",
            "Safe self-starting without checking baseline androgen levels",
            "Benefit if you already have high androgens or PMOS (ex PCOS)"
          ]
        },
        "considerations": [
          "Ask whether the plan is DHEA, testosterone, or another androgen strategy",
          "Ask what baseline labs justify it and how levels will be monitored",
          "Ask what outcome would count as success: follicle count, egg yield, embryos, or live birth",
          "Avoid unsupervised DHEA if you have acne, hair growth, PMOS (ex PCOS), high testosterone, or hormone-sensitive conditions"
        ],
        "bestFor": [
          "Selected poor ovarian response discussions",
          "Low androgen levels with clinician monitoring",
          "Not recommended as routine IVF add-on"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "HFEA rates androgen supplementation grey for improving the chance of having a baby for most fertility patients",
            "source": "HFEA treatment add-ons guidance 2026"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not support routine use of stimulation adjuncts such as DHEA or testosterone",
            "mechanism": "Studies vary by patient selection, dose, timing, and outcome, so routine live birth benefit remains unproven",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023"
          }
        ],
        "headlineStat": "HFEA grey"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        },
        {
          "title": "Treatment add-ons with limited evidence",
          "journal": "HFEA treatment add-ons guidance",
          "year": 2026,
          "url": "https://www.hfea.gov.uk/treatments/treatment-add-ons/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-ovarian-stimulation-adjuncts",
      "kind": "therapy",
      "tags": ["her", "pre-cycle", "during-cycle"],
      "title": "Ovarian Stimulation Adjuncts",
      "icon": "💊",
      "body": {
        "name": "Ovarian Stimulation Adjuncts",
        "category": "clinical",
        "description": "Extra medications or supplements added before or during stimulation, such as growth hormone, DHEA, testosterone, aspirin, metformin, or sildenafil, usually with the goal of improving egg yield or response.",
        "howItWorks": "These adjuncts target different pathways: androgen priming, insulin signalling, blood flow, inflammation, or follicle responsiveness. Some may be appropriate for a separate medical diagnosis, but routine use as IVF add-ons is not supported by strong evidence.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 50,
          "high": 3000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A clinic may suggest one of these when response has been poor",
            "Some are inexpensive; others add substantial medication cost"
          ],
          "shouldNotExpect": [
            "Reliable improvement in live birth from routine add-on use",
            "One adjunct that fixes poor response for everyone"
          ]
        },
        "considerations": [
          "Ask which diagnosis the adjunct is treating",
          "Separate evidence for a medical indication, such as PMOS/metformin (ex PCOS), from routine IVF add-on use",
          "Ask what outcome the clinic expects to improve: egg number, embryo number, or live birth"
        ],
        "bestFor": ["Individualized medical indications", "Research setting"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "ESHRE does not support routine use of stimulation adjuncts such as metformin, growth hormone, testosterone, DHEA, aspirin, indomethacin, or sildenafil",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Not routine"
      },
      "bodyFr": {
        "name": "Adjuvants de stimulation ovarienne",
        "category": "clinical",
        "description": "Médicaments ou compléments ajoutés avant ou pendant la stimulation (hormone de croissance, DHEA, testostérone, aspirine, metformine, sildénafil…), généralement pour améliorer le rendement ovocytaire ou la réponse ovarienne.",
        "howItWorks": "Ces adjuvants ciblent différentes voies : priming androgénique, signalisation insulinique, circulation, inflammation, ou réceptivité folliculaire. Certains peuvent être adaptés à un diagnostic médical à part, mais leur usage de routine en add-on de FIV n'est pas soutenu par des preuves solides.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 30,
          "high": 2000
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Une clinique peut en proposer un quand la réponse a été faible",
            "Certains sont peu coûteux ; d'autres représentent un coût médicamenteux important"
          ],
          "shouldNotExpect": [
            "Une amélioration fiable de la naissance vivante par un usage de routine en add-on",
            "Un adjuvant universel qui résoudrait la mauvaise réponse pour tout le monde"
          ]
        },
        "considerations": [
          "Demande quel diagnostic l'adjuvant traite réellement",
          "Sépare les preuves pour une vraie indication médicale (ex. metformine pour le SOPK/PMOS) de l'usage de routine comme add-on FIV",
          "Demande quel résultat la clinique espère améliorer : nombre d'ovocytes, nombre d'embryons, ou naissance vivante"
        ],
        "bestFor": [
          "Indications médicales individualisées",
          "Cadre de recherche"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "L'ESHRE ne soutient pas l'usage de routine des adjuvants de stimulation (metformine, hormone de croissance, testostérone, DHEA, aspirine, indométacine, sildénafil)",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Pas en routine"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-duostim",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "DuoStim",
      "icon": "⏱️",
      "body": {
        "name": "DuoStim",
        "category": "clinical",
        "description": "Two ovarian stimulation and retrieval attempts in the same menstrual cycle: one in the follicular phase and one in the luteal phase.",
        "howItWorks": "DuoStim is designed to collect more eggs in less calendar time. It may be considered when time is urgent, such as fertility preservation before cancer treatment, but routine use for poor response remains uncertain.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "costRange": {
          "low": 8000,
          "high": 20000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A compressed timeline with two retrievals in one cycle",
            "Potentially more eggs banked quickly"
          ],
          "shouldNotExpect": [
            "Clear proof that routine use improves live birth",
            "Lower treatment burden; it can be physically and financially intense"
          ]
        },
        "considerations": [
          "Most relevant when time is urgent",
          "Ask whether this is being recommended for fertility preservation or poor response"
        ],
        "bestFor": [
          "Urgent fertility preservation",
          "Selected poor responders"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "ESHRE says DuoStim may be considered for urgent fertility preservation but is not recommended for routine clinical use",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Selected cases"
      },
      "bodyFr": {
        "name": "DuoStim",
        "category": "clinical",
        "description": "Deux stimulations et ponctions ovariennes dans le même cycle menstruel : une en phase folliculaire et une en phase lutéale.",
        "howItWorks": "Le DuoStim est pensé pour recueillir plus d'ovocytes en moins de temps calendaire. Il peut être envisagé quand le temps presse, par exemple pour préserver la fertilité avant un traitement anticancéreux, mais son usage de routine en cas de mauvaise réponse reste incertain.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "costRange": {
          "low": 6000,
          "high": 14000
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Un calendrier compressé avec deux ponctions en un cycle",
            "Potentiellement plus d'ovocytes mis de côté rapidement"
          ],
          "shouldNotExpect": [
            "Aucune preuve claire qu'un usage de routine améliore la naissance vivante",
            "Une charge de traitement allégée : ça peut être lourd physiquement et financièrement"
          ]
        },
        "considerations": [
          "Surtout pertinent quand le temps est compté",
          "Demande si c'est proposé pour préserver la fertilité ou pour une mauvaise réponse"
        ],
        "bestFor": [
          "Préservation urgente de la fertilité",
          "Mauvaises répondeuses sélectionnées"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "L'ESHRE indique que le DuoStim peut être envisagé pour une préservation urgente de la fertilité, mais n'est pas recommandé en routine",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Cas sélectionnés"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-ivm",
      "kind": "therapy",
      "tags": ["her", "during-cycle"],
      "title": "In Vitro Maturation (IVM)",
      "icon": "🥚",
      "body": {
        "name": "In Vitro Maturation (IVM)",
        "category": "clinical",
        "description": "Retrieving immature eggs and maturing them in the lab, usually as a lower-stimulation approach for selected patients rather than a routine replacement for standard IVF.",
        "howItWorks": "Instead of relying fully on ovarian stimulation to mature eggs before retrieval, immature eggs are collected and matured in culture. Modern approaches may use hormonal priming, improved culture systems, or pre-IVM steps to better support both nuclear and cytoplasmic maturation.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "costRange": {
          "low": 3000,
          "high": 10000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "A lower-stimulation or lab-maturation approach in selected settings",
            "Possible relevance if you have a high antral follicle count (AFC) or PMOS (ex PCOS), to reduce OHSS risk, or for urgent fertility preservation",
            "Better outcomes than older IVM protocols in experienced programs"
          ],
          "shouldNotExpect": [
            "A routine replacement for conventional IVF in every patient",
            "The same results across all clinics - lab expertise and protocol matter a lot",
            "Clear benefit for low-yield cycles or rescue maturation without careful counselling"
          ]
        },
        "considerations": [
          "Ask whether this is standard clinical IVM, rescue IVM, or natural-cycle IVF/M",
          "Ask how the clinic's maturity, fertilization, and live birth rates compare with standard IVF",
          "Ask whether the clinic has specific IVM expertise rather than offering it as a generic add-on"
        ],
        "bestFor": [
          "High AFC or PMOS (ex PCOS)",
          "OHSS risk reduction",
          "Urgent fertility preservation",
          "Specialist IVM programs"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "ASRM states IVM is no longer considered experimental, but should be offered by teams with specific expertise and counselling",
            "mechanism": "IVM can reduce patient burden, injections, monitoring, cost, and OHSS risk in selected high-AFC patients",
            "source": "ASRM Practice Committee Opinion, Fertility and Sterility 2021"
          },
          {
            "direction": "positive",
            "outcome": "Recent reviews describe improved pregnancy success and neonatal/infant outcomes with newer IVM protocols",
            "mechanism": "Hormonal priming, optimized culture media, and pre-IVM steps may improve the egg's ability to develop into a viable embryo",
            "source": "Biology of Reproduction review, 2025"
          },
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend clinical IVM, rescue IVM, or natural-cycle IVF/M for routine clinical use",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Selected cases"
      },
      "bodyFr": {
        "name": "Maturation in vitro (IVM)",
        "category": "clinical",
        "description": "Récupération d'ovocytes immatures et maturation au laboratoire, généralement comme approche à stimulation réduite chez des patientes sélectionnées, plutôt que comme remplacement de routine de la FIV classique.",
        "howItWorks": "Au lieu de tout miser sur la stimulation ovarienne pour faire mûrir les ovocytes avant ponction, on prélève des ovocytes immatures et on les fait mûrir en culture. Les approches modernes peuvent utiliser un priming hormonal, des milieux de culture optimisés ou des étapes pré-IVM pour soutenir à la fois la maturation nucléaire et cytoplasmique.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "costRange": {
          "low": 2000,
          "high": 7000
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Approche à stimulation réduite ou maturation en laboratoire dans des situations sélectionnées",
            "Pertinence possible si tu as un compte folliculaire antral (CFA) élevé ou un SOPK (PMOS), pour limiter le risque d'OHSS, ou pour une préservation urgente de la fertilité",
            "De meilleurs résultats qu'avec les anciens protocoles d'IVM dans les programmes expérimentés"
          ],
          "shouldNotExpect": [
            "Un remplacement systématique de la FIV conventionnelle pour toutes les patientes",
            "Des résultats équivalents dans toutes les cliniques : l'expertise du laboratoire et le protocole comptent beaucoup",
            "Un bénéfice clair pour les cycles à faible rendement ou la maturation de rattrapage sans conseil personnalisé"
          ]
        },
        "considerations": [
          "Demande s'il s'agit d'IVM clinique standard, d'IVM de rattrapage, ou de FIV en cycle naturel + IVM",
          "Demande comment se comparent les taux de maturation, fécondation et naissance vivante de la clinique avec la FIV standard",
          "Demande si la clinique a une expertise spécifique en IVM, et ne propose pas cela comme un simple add-on générique"
        ],
        "bestFor": [
          "Fort CFA ou SOPK (PMOS)",
          "Réduction du risque d'OHSS",
          "Préservation urgente de la fertilité",
          "Programmes spécialisés en IVM"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "L'ASRM considère que l'IVM n'est plus expérimentale, mais doit être proposée par des équipes avec une expertise et un accompagnement spécifiques",
            "mechanism": "L'IVM peut réduire la charge de traitement, les injections, la surveillance, le coût, et le risque d'OHSS chez certaines patientes à fort CFA",
            "source": "ASRM Practice Committee Opinion, Fertility and Sterility 2021"
          },
          {
            "direction": "positive",
            "outcome": "Les revues récentes décrivent une amélioration du succès des grossesses et des résultats néonataux avec les nouveaux protocoles d'IVM",
            "mechanism": "Le priming hormonal, des milieux de culture optimisés et des étapes pré-IVM peuvent améliorer la capacité de l'ovocyte à se développer en embryon viable",
            "source": "Biology of Reproduction review, 2025"
          },
          {
            "direction": "negative",
            "outcome": "L'ESHRE ne recommande pas l'IVM clinique, l'IVM de rattrapage, ni la FIV en cycle naturel + IVM en usage clinique de routine",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Cas sélectionnés"
      },
      "sources": [
        {
          "title": "In vitro maturation: a committee opinion",
          "journal": "Fertility and Sterility",
          "year": 2021,
          "evidenceLevel": "review"
        },
        {
          "title": "In vitro maturation of oocytes: what is already known?",
          "journal": "Biology of Reproduction",
          "year": 2025,
          "evidenceLevel": "review"
        },
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-experimental-rejuvenation",
      "kind": "therapy",
      "tags": ["her", "pre-cycle"],
      "title": "Experimental Rejuvenation",
      "icon": "🌱",
      "body": {
        "name": "Experimental Rejuvenation",
        "category": "clinical",
        "description": "Highly experimental interventions such as stem cell therapy, in vitro activation of dormant follicles, or mitochondrial replacement approaches marketed for poor ovarian reserve, POI, thin lining, or egg quality.",
        "howItWorks": "These interventions aim to regenerate or reactivate ovarian or endometrial tissue, or to improve oocyte quality through cellular or mitochondrial approaches. The biology is still investigational, and procedures may be invasive.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 5000,
          "high": 30000
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "These may be marketed to people with few remaining options",
            "Some early case reports or uncontrolled studies may sound promising"
          ],
          "shouldNotExpect": [
            "Established improvement in live birth",
            "Clear long-term safety data",
            "Routine endorsement from major reproductive medicine societies"
          ]
        },
        "considerations": [
          "Only consider this in a registered research protocol with explicit safety monitoring",
          "Be cautious of high-cost packages promising ovarian rejuvenation",
          "Ask for controlled outcome data, not only testimonials or before-and-after hormone values"
        ],
        "bestFor": ["Research protocols only"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "ESHRE does not recommend stem cell therapy, in vitro activation, or mitochondrial replacement therapy as routine add-ons for infertility treatment",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Experimental"
      },
      "bodyFr": {
        "name": "Rajeunissement expérimental",
        "category": "clinical",
        "description": "Interventions très expérimentales (thérapie par cellules souches, activation in vitro des follicules dormants, remplacement mitochondrial) commercialisées pour la réserve ovarienne basse, l'IOP, l'endomètre fin ou la qualité ovocytaire.",
        "howItWorks": "Ces interventions visent à régénérer ou réactiver les tissus ovariens ou endométriaux, ou à améliorer la qualité ovocytaire par des approches cellulaires ou mitochondriales. La biologie reste exploratoire et les procédures peuvent être invasives.",
        "evidenceStrength": "debunked",
        "forWhom": "her",
        "costRange": {
          "low": 3500,
          "high": 20000
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Souvent proposé à des personnes qui ont peu d'options restantes",
            "Certains rapports de cas ou études non contrôlées peuvent paraître prometteurs"
          ],
          "shouldNotExpect": [
            "Une amélioration établie de la naissance vivante",
            "Des données de sécurité claires à long terme",
            "Un soutien de routine des grandes sociétés savantes en médecine de la reproduction"
          ]
        },
        "considerations": [
          "À envisager uniquement dans le cadre d'un protocole de recherche enregistré avec une surveillance de sécurité explicite",
          "Méfie-toi des packs coûteux promettant un « rajeunissement ovarien »",
          "Demande des données de résultats contrôlées, pas seulement des témoignages ou des bilans hormonaux avant/après"
        ],
        "bestFor": ["Protocoles de recherche uniquement"],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "L'ESHRE ne recommande ni la thérapie par cellules souches, ni l'activation in vitro, ni la thérapie de remplacement mitochondrial comme add-ons de routine en infertilité",
            "source": "ESHRE Good Practice Recommendations on Add-ons, 2023",
            "sourceUrl": "https://academic.oup.com/humrep/article/38/11/2062/7281712"
          }
        ],
        "headlineStat": "Expérimental"
      },
      "sources": [
        {
          "title": "Good practice recommendations on add-ons in reproductive medicine",
          "journal": "Human Reproduction",
          "year": 2023,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-pelvic-floor",
      "kind": "therapy",
      "tags": ["her", "optimization", "pre-cycle"],
      "title": "Pelvic Floor Physiotherapy",
      "icon": "🏋️‍♀️",
      "body": {
        "name": "Pelvic Floor Physiotherapy",
        "category": "physical",
        "description": "Specialized physiotherapy focusing on the muscles, ligaments, and connective tissue of the pelvic floor to address pain, tension, and dysfunction that may affect fertility and pregnancy.",
        "howItWorks": "A pelvic floor physiotherapist assesses muscle tone, strength, and coordination. Treatment may include manual therapy, biofeedback, exercises, and relaxation techniques. Addressing pelvic floor dysfunction can reduce pain during intercourse, improve blood flow, and prepare the body for pregnancy.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "sessionDuration": "45-60 minutes",
        "frequencyRange": "Weekly for 6-12 weeks",
        "costRange": {
          "low": 100,
          "high": 250
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Assessment of pelvic floor muscle function and any dysfunction",
            "Reduced pain during intercourse if related to muscle tension",
            "Improved pelvic awareness and body connection",
            "Better preparation for pregnancy and labor"
          ],
          "shouldNotExpect": [
            "This is not a direct fertility treatment - it addresses physical function and comfort",
            "It requires active participation with home exercises between sessions",
            "You may need several sessions before noticing improvements"
          ]
        },
        "considerations": [
          "Especially beneficial if you experience painful intercourse (dyspareunia)",
          "Can help with endometriosis-related pelvic pain",
          "Many practitioners also specialize in prenatal and postpartum care",
          "Often covered by health insurance with a referral"
        ],
        "bestFor": [
          "Painful intercourse",
          "Endometriosis pain",
          "Pregnancy preparation",
          "Post-surgical recovery"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "In an RCT comparing pelvic floor rehabilitation (electrotherapy, manual therapy, exercises) to a waitlist control in women with dyspareunia, the intervention group showed significant reductions in pain and improvements in sexual function and pelvic floor muscle strength",
            "magnitude": "VAS pain reduction mean difference 7.32; FSFI sexual function score improvement 51.05",
            "mechanism": "Manual release of hypertonic pelvic floor muscles, desensitization of trigger points, and biofeedback retraining of muscle coordination",
            "source": "Ghaderi et al., International Urogynecology Journal 2019 (RCT, n=64)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC6834927/"
          },
          {
            "direction": "positive",
            "outcome": "A systematic review and meta-analysis of 7 studies found that physiotherapy techniques significantly reduce endometriosis-associated pelvic pain, especially when applied locally",
            "source": "Can et al., Pain Medicine 2026 (systematic review and meta-analysis, 7 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/40705433/"
          },
          {
            "direction": "neutral",
            "outcome": "There is no direct evidence that pelvic floor physiotherapy improves clinical pregnancy or live birth - benefits are demonstrated for pain, dyspareunia, and pelvic floor function, not for fertility outcomes per se"
          }
        ],
        "headlineStat": "Reduces pelvic pain, not a fertility treatment"
      },
      "bodyFr": {
        "name": "Rééducation du périnée",
        "category": "physical",
        "description": "Kinésithérapie spécialisée centrée sur les muscles, ligaments et tissus conjonctifs du plancher pelvien, pour traiter douleurs, tensions et dysfonctions qui peuvent affecter la fertilité et la grossesse.",
        "howItWorks": "Un·e kinésithérapeute ou sage-femme spécialisé·e évalue le tonus, la force et la coordination des muscles. Le traitement combine thérapie manuelle, biofeedback, exercices et techniques de relaxation. Traiter une dysfonction du périnée peut réduire les douleurs aux rapports, améliorer la circulation et préparer le corps à la grossesse.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "sessionDuration": "45 à 60 minutes",
        "frequencyRange": "Une fois par semaine pendant 6 à 12 semaines",
        "costRange": {
          "low": 60,
          "high": 90
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Une évaluation du fonctionnement et des éventuelles dysfonctions du périnée",
            "Une réduction des douleurs aux rapports si elles sont liées à des tensions musculaires",
            "Une meilleure conscience du périnée et du corps",
            "Une meilleure préparation à la grossesse et à l'accouchement"
          ],
          "shouldNotExpect": [
            "Ce n'est pas un traitement direct de la fertilité : ça agit sur la fonction physique et le confort",
            "Ça demande une implication active avec des exercices à la maison entre les séances",
            "Plusieurs séances peuvent être nécessaires avant de ressentir des améliorations"
          ]
        },
        "considerations": [
          "Particulièrement utile en cas de douleurs aux rapports (dyspareunie)",
          "Peut aider à soulager les douleurs pelviennes liées à l'endométriose",
          "Beaucoup de praticien·nes sont aussi spécialisé·es en prénatal et post-partum",
          "Souvent prise en charge par la Sécurité sociale et la mutuelle sur prescription"
        ],
        "bestFor": [
          "Douleurs aux rapports",
          "Douleurs liées à l'endométriose",
          "Préparation à la grossesse",
          "Récupération post-chirurgicale"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Dans un essai randomisé comparant la rééducation périnéale (électrothérapie, thérapie manuelle, exercices) à un groupe en liste d'attente chez des femmes avec dyspareunie, le groupe traité a montré des réductions significatives de la douleur et des améliorations de la fonction sexuelle et de la force du périnée",
            "magnitude": "Réduction de douleur EVA : différence moyenne de 7,32 ; amélioration du score FSFI de fonction sexuelle de 51,05",
            "mechanism": "Libération manuelle des muscles hypertoniques, désensibilisation des points-gâchettes, reprogrammation de la coordination musculaire par biofeedback",
            "source": "Ghaderi et al., International Urogynecology Journal 2019 (RCT, n=64)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC6834927/"
          },
          {
            "direction": "positive",
            "outcome": "Une revue systématique et méta-analyse de 7 études a trouvé que les techniques de kinésithérapie réduisent significativement les douleurs pelviennes liées à l'endométriose, surtout en application locale",
            "source": "Can et al., Pain Medicine 2026 (systematic review and meta-analysis, 7 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/40705433/"
          },
          {
            "direction": "neutral",
            "outcome": "Aucune preuve directe que la rééducation périnéale améliore la grossesse clinique ou la naissance vivante : les bénéfices démontrés portent sur la douleur, la dyspareunie et la fonction du périnée, pas sur la fertilité en tant que telle"
          }
        ],
        "headlineStat": "Réduit la douleur, pas un traitement de fertilité"
      },
      "sources": [
        {
          "title": "Pelvic floor physiotherapy for women with pelvic pain: a systematic review",
          "journal": "Journal of Sexual Medicine",
          "year": 2019,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-fertility-massage",
      "kind": "therapy",
      "tags": ["her", "optimization", "pre-cycle"],
      "title": "Fertility Massage",
      "icon": "💆‍♀️",
      "body": {
        "name": "Fertility Massage (Maya Abdominal Therapy)",
        "category": "physical",
        "description": "Specialized abdominal massage techniques designed to improve blood flow to reproductive organs, break up adhesions, and support uterine alignment.",
        "howItWorks": "Practitioners use gentle external abdominal massage to improve circulation to the uterus and ovaries, help relieve congestion, and address any uterine malposition. Techniques may also include castor oil packs and self-care massage instruction.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "sessionDuration": "60-90 minutes",
        "frequencyRange": "Every 1-2 weeks for 3 months pre-conception",
        "costRange": {
          "low": 80,
          "high": 180
        },
        "currency": "USD",
        "expectations": {
          "canExpect": [
            "Deeply relaxing abdominal and pelvic area massage",
            "May improve circulation to reproductive organs",
            "Self-care techniques you can practice at home"
          ],
          "shouldNotExpect": [
            "There is very limited clinical evidence for fertility-specific outcomes",
            "Claims about breaking up adhesions or repositioning the uterus are not supported by evidence",
            "This should not be performed during menstruation, pregnancy, or active IVF stimulation"
          ]
        },
        "considerations": [
          "Avoid during the two-week wait and active treatment cycles",
          "Look for practitioners specifically trained in Maya Abdominal Therapy",
          "Can be a relaxing complement to medical treatment"
        ],
        "bestFor": [
          "Pre-conception preparation",
          "Stress reduction",
          "Pelvic circulation"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "The most-cited fertility massage study (Wurn et al.) is a retrospective case series of 28 women with confirmed bilateral fallopian tube occlusion. The authors reported 61% restored tubal patency and several natural pregnancies after manual pelvic physical therapy - but this was uncontrolled, not blinded, and conducted by the technique's developers",
            "magnitude": "Retrospective: 17/28 women regained tubal patency; ~half conceived within 2 years",
            "source": "Wurn et al., Alternative Therapies in Health and Medicine 2008 (retrospective case series, n=28)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/18251317/"
          },
          {
            "direction": "neutral",
            "outcome": "No randomized controlled trial has tested whether fertility massage improves pregnancy rates, and claims about external massage 'breaking up adhesions' or 'repositioning' the uterus are not supported by controlled imaging studies",
            "mechanism": "External abdominal massage is unlikely to reach or physically alter pelvic adhesions or change uterine position in any durable way"
          },
          {
            "direction": "positive",
            "outcome": "Like other forms of touch therapy, abdominal massage may reduce stress and promote relaxation - this is the only effect with reasonable plausibility from controlled massage research outside the fertility context",
            "mechanism": "Parasympathetic activation through touch and relaxation"
          }
        ],
        "headlineStat": "Very limited evidence"
      },
      "bodyFr": {
        "name": "Massage fertilité (thérapie abdominale Maya)",
        "category": "physical",
        "description": "Techniques de massage abdominal spécialisées visant à améliorer la circulation vers les organes reproducteurs, à assouplir les adhérences et à soutenir l'alignement utérin.",
        "howItWorks": "Les praticien·nes utilisent un massage abdominal externe doux pour améliorer la circulation vers l'utérus et les ovaires, aider à soulager la congestion et corriger une éventuelle malposition utérine. Les techniques peuvent inclure des cataplasmes d'huile de ricin et un apprentissage d'auto-massage.",
        "evidenceStrength": "emerging",
        "forWhom": "her",
        "sessionDuration": "60 à 90 minutes",
        "frequencyRange": "Toutes les 1 à 2 semaines pendant 3 mois pré-conception",
        "costRange": {
          "low": 60,
          "high": 120
        },
        "currency": "EUR",
        "expectations": {
          "canExpect": [
            "Un massage abdominal et pelvien profondément relaxant",
            "Une amélioration possible de la circulation vers les organes reproducteurs",
            "Des techniques d'auto-soin à pratiquer à la maison"
          ],
          "shouldNotExpect": [
            "Très peu de preuves cliniques sur des résultats spécifiques de fertilité",
            "Les promesses de « casser » les adhérences ou de repositionner l'utérus ne sont pas soutenues par les preuves",
            "À éviter pendant les règles, pendant la grossesse, et pendant une stimulation FIV en cours"
          ]
        },
        "considerations": [
          "À éviter pendant l'attente des 2 semaines et pendant les cycles de traitement actif",
          "Cherche un·e praticien·ne spécifiquement formé·e à la thérapie abdominale Maya",
          "Peut être un complément relaxant au suivi médical"
        ],
        "bestFor": [
          "Préparation pré-conception",
          "Réduction du stress",
          "Circulation pelvienne"
        ],
        "keyFindings": [
          {
            "direction": "neutral",
            "outcome": "L'étude la plus citée sur le massage fertilité (Wurn et al.) est une série de cas rétrospective de 28 femmes avec obstruction tubaire bilatérale confirmée. Les auteurs rapportent une perméabilité tubaire restaurée chez 61 % et plusieurs grossesses naturelles après thérapie pelvienne manuelle, mais l'étude est non contrôlée, non en aveugle, et conduite par les conceptrices de la technique",
            "magnitude": "Rétrospectif : 17/28 femmes avec perméabilité tubaire restaurée ; ~la moitié ont conçu en 2 ans",
            "source": "Wurn et al., Alternative Therapies in Health and Medicine 2008 (retrospective case series, n=28)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/18251317/"
          },
          {
            "direction": "neutral",
            "outcome": "Aucun essai randomisé n'a testé si le massage fertilité améliore les taux de grossesse, et les affirmations selon lesquelles un massage externe « casserait » les adhérences ou « repositionnerait » l'utérus ne sont pas soutenues par des études d'imagerie contrôlées",
            "mechanism": "Un massage abdominal externe ne peut probablement ni atteindre ni modifier durablement les adhérences pelviennes ou la position utérine"
          },
          {
            "direction": "positive",
            "outcome": "Comme d'autres formes de thérapie par le toucher, le massage abdominal peut réduire le stress et favoriser la relaxation : c'est le seul effet plausible au vu des données sur le massage en dehors du contexte fertilité",
            "mechanism": "Activation parasympathique par le toucher et la relaxation"
          }
        ],
        "headlineStat": "Très peu de preuves"
      },
      "sources": [
        {
          "title": "Manual therapy for female reproductive health: a scoping review",
          "journal": "Journal of Bodywork and Movement Therapies",
          "year": 2020,
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "therapy-mediterranean-diet",
      "kind": "therapy",
      "tags": [
        "both-partners",
        "optimization",
        "pre-cycle",
        "evidence-based",
        "lifestyle"
      ],
      "title": "Mediterranean Diet",
      "icon": "🫒",
      "body": {
        "name": "Mediterranean Diet for Fertility",
        "category": "nutritional",
        "description": "A dietary pattern rich in fruits, vegetables, whole grains, legumes, fish, and olive oil with strong evidence for improving fertility outcomes in both women and men.",
        "howItWorks": "The Mediterranean diet is rich in antioxidants, omega-3 fatty acids, and anti-inflammatory compounds that protect egg and sperm quality. It improves insulin sensitivity, reduces inflammation, and provides essential micronutrients for reproductive health.",
        "evidenceStrength": "strong",
        "forWhom": "both",
        "frequencyRange": "Daily - this is a dietary pattern, not a short-term diet",
        "expectations": {
          "canExpect": [
            "Improved IVF outcomes - studies show higher clinical pregnancy and live birth rates",
            "Better sperm quality parameters in men",
            "Improved insulin sensitivity, especially relevant for PMOS (ex PCOS)",
            "Overall health benefits beyond fertility"
          ],
          "shouldNotExpect": [
            "This is not a quick fix - dietary changes take months to affect egg and sperm quality",
            "Diet does not replace medical treatment for fertility conditions",
            "It requires sustained dietary change, not a short-term intervention"
          ]
        },
        "considerations": [
          "Focus on whole foods: vegetables, fruits, legumes, fish, olive oil, nuts",
          "Limit processed foods, red meat, and added sugars",
          "Not a restrictive diet - it emphasizes abundance and variety",
          "Pair with a prenatal vitamin for complete micronutrient coverage"
        ],
        "bestFor": [
          "IVF preparation",
          "PMOS (ex PCOS) management",
          "Sperm quality improvement",
          "Overall reproductive health"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "In a prospective cohort of non-obese women <35 years undergoing their first IVF cycle, higher adherence to the Mediterranean diet was associated with a significantly higher chance of clinical pregnancy and live birth",
            "magnitude": "Women in the top third of Mediterranean diet adherence had ~2.7× the odds of clinical pregnancy and live birth compared with the bottom third (in women under 35)",
            "mechanism": "Rich in antioxidants (folate, B6, B12), omega-3 fatty acids, and anti-inflammatory compounds that may protect eggs from oxidative damage and help prepare the uterine lining to accept an embryo",
            "source": "Karayiannis et al., Human Reproduction 2018 (prospective cohort, n=244)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/29390148/"
          },
          {
            "direction": "positive",
            "outcome": "In couples undergoing IVF/ICSI, joint preconception adherence to a Mediterranean dietary pattern was associated with a higher chance of pregnancy",
            "magnitude": "Pregnancy OR 1.4 (95% CI 1.0-1.9) per unit increase in joint Mediterranean diet score",
            "source": "Vujkovic et al., Fertility and Sterility 2010 (prospective cohort, n=161 couples)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/20189169/"
          },
          {
            "direction": "neutral",
            "outcome": "Evidence in men is largely observational. Mediterranean-style diets are associated with better semen parameters in cross-sectional studies, but causality has not been firmly established through RCTs",
            "mechanism": "Antioxidants may reduce sperm DNA oxidative damage; omega-3s contribute to sperm membrane fluidity"
          }
        ],
        "headlineStat": "↑pregnancy in younger women",
        "relatedSupplements": {
          "ids": [
            "supplement-omega-3",
            "supplement-methylfolate",
            "supplement-vitamin-d",
            "supplement-coq10",
            "supplement-vitamin-b-complex",
            "supplement-vitamin-e"
          ],
          "patternNote": "The Mediterranean diet's benefits come from the overall dietary pattern - the synergy of whole foods, fiber, polyphenols, and healthy fats working together - not from any single nutrient. Studies consistently show that the food matrix matters: nutrients from whole foods are absorbed and used differently than isolated supplements. That said, if you struggle to consistently follow this pattern, targeted supplements can help fill specific gaps. They support the diet - they don't replace it."
        }
      },
      "bodyFr": {
        "name": "Régime méditerranéen pour la fertilité",
        "category": "nutritional",
        "description": "Profil alimentaire riche en fruits, légumes, céréales complètes, légumineuses, poisson et huile d'olive, avec des preuves solides d'amélioration de la fertilité chez les femmes comme chez les hommes.",
        "howItWorks": "Le régime méditerranéen est riche en antioxydants, en oméga-3 et en composés anti-inflammatoires qui protègent la qualité des ovocytes et des spermatozoïdes. Il améliore la sensibilité à l'insuline, réduit l'inflammation et apporte les micronutriments essentiels à la santé reproductive.",
        "evidenceStrength": "strong",
        "forWhom": "both",
        "frequencyRange": "Quotidien : c'est un mode d'alimentation, pas un régime court",
        "expectations": {
          "canExpect": [
            "Meilleurs résultats de FIV : des études montrent des taux de grossesse clinique et de naissance vivante plus élevés",
            "Meilleure qualité spermatique chez les hommes",
            "Meilleure sensibilité à l'insuline, particulièrement utile en cas de SOPK (PMOS)",
            "Bénéfices généraux pour la santé au-delà de la fertilité"
          ],
          "shouldNotExpect": [
            "Ce n'est pas une solution rapide : les changements alimentaires prennent des mois pour agir sur la qualité ovocytaire et spermatique",
            "L'alimentation ne remplace pas le traitement médical des pathologies de fertilité",
            "Demande un changement durable, pas une intervention courte"
          ]
        },
        "considerations": [
          "Privilégie les aliments bruts : légumes, fruits, légumineuses, poisson, huile d'olive, fruits à coque",
          "Limite les produits transformés, la viande rouge et les sucres ajoutés",
          "Pas un régime restrictif : il met l'accent sur l'abondance et la variété",
          "À associer à une vitamine prénatale pour couvrir tous les micronutriments"
        ],
        "bestFor": [
          "Préparation à la FIV",
          "Gestion du SOPK (PMOS)",
          "Amélioration de la qualité spermatique",
          "Santé reproductive globale"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Dans une cohorte prospective de femmes non obèses de moins de 35 ans en première FIV, une meilleure adhésion au régime méditerranéen était associée à un taux de grossesse clinique et de naissance vivante significativement plus élevés",
            "magnitude": "Les femmes du tiers supérieur d'adhésion au régime méditerranéen avaient ~2,7× plus de chances de grossesse clinique et de naissance vivante que celles du tiers inférieur (chez les moins de 35 ans)",
            "mechanism": "Riche en antioxydants (folates, B6, B12), en oméga-3 et en composés anti-inflammatoires qui peuvent protéger les ovocytes du stress oxydatif et aider la muqueuse utérine à accueillir l'embryon",
            "source": "Karayiannis et al., Human Reproduction 2018 (prospective cohort, n=244)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/29390148/"
          },
          {
            "direction": "positive",
            "outcome": "Chez les couples en FIV/ICSI, une adhésion conjointe préconceptionnelle à un profil méditerranéen était associée à une probabilité de grossesse plus élevée",
            "magnitude": "Grossesse OR 1,4 (IC 95 % 1,0-1,9) par point gagné sur le score méditerranéen conjoint",
            "source": "Vujkovic et al., Fertility and Sterility 2010 (prospective cohort, n=161 couples)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/20189169/"
          },
          {
            "direction": "neutral",
            "outcome": "Chez les hommes, les preuves restent essentiellement observationnelles. Les régimes de type méditerranéen sont associés à de meilleurs paramètres spermatiques dans des études transversales, mais la causalité n'a pas été solidement établie par des essais randomisés",
            "mechanism": "Les antioxydants peuvent réduire les dommages oxydatifs de l'ADN spermatique ; les oméga-3 contribuent à la fluidité membranaire"
          }
        ],
        "headlineStat": "↑grossesse chez les <35 ans",
        "relatedSupplements": {
          "ids": [
            "supplement-omega-3",
            "supplement-methylfolate",
            "supplement-vitamin-d",
            "supplement-coq10",
            "supplement-vitamin-b-complex",
            "supplement-vitamin-e"
          ],
          "patternNote": "Les bénéfices du régime méditerranéen viennent du profil alimentaire global (la synergie entre aliments bruts, fibres, polyphénols et bonnes graisses), pas d'un seul nutriment. Les études montrent que la matrice alimentaire compte : les nutriments des aliments entiers sont absorbés et utilisés différemment de ceux des compléments isolés. Cela dit, si tu as du mal à suivre ce profil de manière régulière, des compléments ciblés peuvent combler des manques précis. Ils soutiennent le régime, ils ne le remplacent pas."
        }
      },
      "sources": [
        {
          "title": "Adherence to the Mediterranean diet and IVF outcomes: a prospective cohort study",
          "journal": "Human Reproduction",
          "year": 2018,
          "evidenceLevel": "cohort"
        },
        {
          "title": "Mediterranean dietary pattern and fertility: a systematic review",
          "journal": "Fertility and Sterility",
          "year": 2019,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-anti-inflammatory-diet",
      "kind": "therapy",
      "tags": ["her", "optimization", "pre-cycle"],
      "title": "Anti-Inflammatory Diet",
      "icon": "🥗",
      "body": {
        "name": "Anti-Inflammatory Diet",
        "category": "nutritional",
        "description": "A dietary approach focused on reducing systemic inflammation, particularly beneficial for endometriosis, PMOS (ex PCOS), and unexplained infertility where chronic inflammation may play a role.",
        "howItWorks": "Chronic low-grade inflammation can affect ovulation, egg quality, implantation, and sperm function. An anti-inflammatory diet emphasizes foods that lower markers of inflammation in the blood (like CRP and IL-6) and cuts common pro-inflammatory triggers like refined sugars, trans fats, and processed foods.",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "frequencyRange": "Daily dietary pattern",
        "expectations": {
          "canExpect": [
            "Reduced inflammatory markers and improved overall wellbeing",
            "May help manage endometriosis and PMOS (ex PCOS) symptoms",
            "Improved energy levels and reduced bloating",
            "Complements medical treatment for inflammation-related fertility issues"
          ],
          "shouldNotExpect": [
            "This is not a cure for endometriosis or PMOS (ex PCOS) - it is an adjunct to medical care",
            "Individual food sensitivities vary, and what triggers inflammation differs by person",
            "Overly restrictive diets can increase stress - focus on adding good foods, not just eliminating"
          ]
        },
        "considerations": [
          "Consider working with a registered dietitian specializing in fertility",
          "Focus on anti-inflammatory foods: fatty fish, berries, leafy greens, turmeric, ginger",
          "Reduce or eliminate: processed foods, refined sugar, excessive alcohol, trans fats",
          "An elimination approach may help identify personal trigger foods"
        ],
        "bestFor": [
          "Endometriosis",
          "PMOS (ex PCOS)",
          "Chronic inflammation",
          "Unexplained infertility"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "A meta-analysis of 9 observational studies found that more pro-inflammatory diets were associated with significantly higher odds of infertility: each step up on a dietary inflammatory score was linked to ~10% higher infertility risk",
            "magnitude": "Most pro-inflammatory vs least pro-inflammatory diet: 61% higher odds of infertility (pooled odds ratio 1.61)",
            "mechanism": "Chronic low-grade inflammation may impair ovulation, the uterine lining's ability to accept an embryo, and sperm function via elevated inflammatory markers (IL-6, TNF-alpha, CRP), but causality cannot be established from observational data",
            "source": "Zahedi et al., Frontiers in Nutrition 2025 (systematic review and meta-analysis, 9 studies, n=17,711)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC12417118/"
          },
          {
            "direction": "positive",
            "outcome": "A meta-analysis of RCTs of anti-inflammatory dietary supplements (omega-3, vitamins, polyphenols) in endometriosis found a significant reduction in pelvic pain, with the largest effect in women over 32 and with longer (>8 week) supplementation",
            "source": "Naeini et al., International Journal of Gynecology & Obstetrics 2025 (GRADE-assessed meta-analysis of RCTs)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/39692186/"
          },
          {
            "direction": "neutral",
            "outcome": "No RCT has yet shown that switching to an anti-inflammatory diet improves clinical pregnancy or live birth - the evidence supports symptom relief in endometriosis and an association with lower infertility risk, not a proven treatment effect on fertility outcomes",
            "source": "Zahedi et al., Frontiers in Nutrition 2025 (systematic review and meta-analysis, 9 studies, n=17,711)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC12417118/"
          }
        ],
        "headlineStat": "↓inflammation, evidence is associational",
        "relatedSupplements": {
          "ids": [
            "supplement-omega-3",
            "supplement-nac",
            "supplement-vitamin-c",
            "supplement-vitamin-e",
            "supplement-probiotics"
          ],
          "patternNote": "Anti-inflammatory eating is about shifting the overall balance of your diet - more omega-3s, polyphenols, and fiber; fewer processed foods and refined sugars. Whole foods deliver these compounds alongside cofactors and fiber that affect how they are absorbed and used. Supplements can target specific gaps (especially omega-3s if you don't eat fish, or NAC for its glutathione-boosting effects), but they work best on top of the dietary foundation, not instead of it."
        }
      },
      "bodyFr": {
        "name": "Régime anti-inflammatoire",
        "category": "nutritional",
        "description": "Approche alimentaire centrée sur la réduction de l'inflammation systémique, particulièrement utile en cas d'endométriose, de SOPK (PMOS) et d'infertilité inexpliquée où l'inflammation chronique peut jouer un rôle.",
        "howItWorks": "Une inflammation chronique de bas grade peut perturber l'ovulation, la qualité ovocytaire, l'implantation et la fonction spermatique. Le régime anti-inflammatoire met en avant les aliments qui font baisser les marqueurs d'inflammation dans le sang (comme la CRP ou l'IL-6) et réduit les déclencheurs pro-inflammatoires courants (sucres raffinés, graisses trans, ultra-transformés).",
        "evidenceStrength": "moderate",
        "forWhom": "her",
        "frequencyRange": "Profil alimentaire quotidien",
        "expectations": {
          "canExpect": [
            "Une baisse des marqueurs inflammatoires et un meilleur bien-être global",
            "Peut aider à gérer les symptômes de l'endométriose et du SOPK (PMOS)",
            "Plus d'énergie et moins de ballonnements",
            "Complète le suivi médical des pathologies de fertilité liées à l'inflammation"
          ],
          "shouldNotExpect": [
            "Ce n'est pas un traitement curatif de l'endométriose ni du SOPK (PMOS) : c'est un complément à la prise en charge médicale",
            "Les sensibilités alimentaires varient d'une personne à l'autre",
            "Un régime trop restrictif peut augmenter le stress : privilégie l'ajout de bons aliments plutôt que la seule élimination"
          ]
        },
        "considerations": [
          "Envisage de travailler avec un·e diététicien·ne spécialisé·e en fertilité",
          "Mise sur les aliments anti-inflammatoires : poissons gras, baies, légumes-feuilles, curcuma, gingembre",
          "Réduis ou élimine : ultra-transformés, sucre raffiné, alcool en excès, graisses trans",
          "Une approche par élimination peut aider à identifier tes propres déclencheurs"
        ],
        "bestFor": [
          "Endométriose",
          "SOPK (PMOS)",
          "Inflammation chronique",
          "Infertilité inexpliquée"
        ],
        "keyFindings": [
          {
            "direction": "positive",
            "outcome": "Une méta-analyse de 9 études observationnelles a trouvé que les régimes plus pro-inflammatoires étaient associés à un risque d'infertilité significativement plus élevé : chaque point gagné sur un score d'inflammation alimentaire était associé à ~10 % de risque en plus",
            "magnitude": "Régime le plus pro-inflammatoire vs le moins : 61 % de risque d'infertilité en plus (rapport de cotes poolé 1,61)",
            "mechanism": "L'inflammation chronique de bas grade pourrait altérer l'ovulation, l'aptitude de la muqueuse utérine à accueillir l'embryon et la fonction spermatique via une élévation des marqueurs inflammatoires (IL-6, TNF-alpha, CRP), mais la causalité ne peut pas être établie à partir de données observationnelles",
            "source": "Zahedi et al., Frontiers in Nutrition 2025 (systematic review and meta-analysis, 9 studies, n=17,711)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC12417118/"
          },
          {
            "direction": "positive",
            "outcome": "Une méta-analyse d'essais randomisés sur les compléments anti-inflammatoires (oméga-3, vitamines, polyphénols) dans l'endométriose a montré une réduction significative de la douleur pelvienne, avec l'effet le plus marqué chez les femmes de plus de 32 ans et avec une supplémentation prolongée (>8 semaines)",
            "source": "Naeini et al., International Journal of Gynecology & Obstetrics 2025 (GRADE-assessed meta-analysis of RCTs)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/39692186/"
          },
          {
            "direction": "neutral",
            "outcome": "Aucun essai randomisé n'a montré pour l'instant que passer à un régime anti-inflammatoire améliore la grossesse clinique ou la naissance vivante : les preuves soutiennent un soulagement des symptômes en endométriose et une association avec un risque d'infertilité plus faible, pas un effet traitement démontré sur la fertilité",
            "source": "Zahedi et al., Frontiers in Nutrition 2025 (systematic review and meta-analysis, 9 studies, n=17,711)",
            "sourceUrl": "https://pmc.ncbi.nlm.nih.gov/articles/PMC12417118/"
          }
        ],
        "headlineStat": "↓inflammation, preuves associatives",
        "relatedSupplements": {
          "ids": [
            "supplement-omega-3",
            "supplement-nac",
            "supplement-vitamin-c",
            "supplement-vitamin-e",
            "supplement-probiotics"
          ],
          "patternNote": "Manger anti-inflammatoire, c'est rééquilibrer l'ensemble de ton alimentation : plus d'oméga-3, de polyphénols et de fibres ; moins d'ultra-transformés et de sucres raffinés. Les aliments entiers apportent ces composés en même temps que des cofacteurs et des fibres qui influencent leur absorption. Les compléments peuvent cibler des manques précis (surtout les oméga-3 si tu ne manges pas de poisson, ou la NAC pour son effet sur le glutathion), mais ils fonctionnent mieux en complément du socle alimentaire, pas à la place."
        }
      },
      "sources": [
        {
          "title": "Dietary inflammatory index and fertility outcomes: a systematic review",
          "journal": "Nutrients",
          "year": 2021,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-sleep-optimization",
      "kind": "therapy",
      "tags": ["both-partners", "optimization", "pre-cycle", "lifestyle"],
      "title": "Sleep Optimization",
      "icon": "😴",
      "body": {
        "name": "Sleep Optimization for Fertility",
        "category": "lifestyle",
        "description": "Structured sleep hygiene practices to optimize circadian rhythm, melatonin production, and hormonal balance - all critical for reproductive health.",
        "howItWorks": "Sleep drives several reproductive hormones: melatonin (a powerful antioxidant that helps protect eggs), the brain signals that release FSH and LH (the hormones that drive ovulation), growth hormone, and cortisol. Poor sleep disrupts these rhythms and is associated with longer time to pregnancy, irregular cycles, and reduced IVF success.",
        "evidenceStrength": "moderate",
        "forWhom": "both",
        "frequencyRange": "Nightly - consistent sleep schedule",
        "expectations": {
          "canExpect": [
            "Improved hormonal regulation including melatonin and cortisol",
            "Better emotional resilience during fertility treatment",
            "Shift work and sleep disruption are associated with menstrual irregularities",
            "7-8 hours of quality sleep is the target for reproductive health"
          ],
          "shouldNotExpect": [
            "Sleep optimization alone cannot overcome significant fertility factors",
            "Changing sleep habits takes weeks of consistency to show hormonal effects",
            "Sleep disturbance from fertility medications is common - be gentle with yourself"
          ]
        },
        "considerations": [
          "Maintain a consistent sleep-wake schedule, even on weekends",
          "Avoid blue light exposure 1-2 hours before bed",
          "Keep bedroom cool (65-68°F / 18-20°C) and dark",
          "Limit caffeine after noon and alcohol in the evening",
          "If you do shift work, discuss with your RE - it may affect treatment timing"
        ],
        "bestFor": [
          "Hormonal balance",
          "IVF preparation",
          "Shift workers",
          "Stress management"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "A systematic review covering 33 studies found that short sleep duration, evening chronotype, and shift/night work were associated with poorer female and male fertility and IVF outcomes, but inconsistent methodology limits firm causal claims",
            "mechanism": "Sleep regulates melatonin (an antioxidant that helps protect eggs), the brain signals controlling FSH and LH (the hormones that drive ovulation), and the daily cortisol rhythm",
            "source": "Caetano et al., Fertility and Sterility 2021 (systematic review, 33 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33054981/"
          },
          {
            "direction": "negative",
            "outcome": "A meta-analysis of 123,403 women found female shift workers had higher rates of menstrual irregularities and subfertility, and night shift was associated with increased early miscarriage risk - though authors note this is not sufficient grounds to advise restricting shift work",
            "magnitude": "Menstrual irregularity OR 1.22; subfertility OR 1.80 (crude); early miscarriage OR 1.29 (night shift)",
            "mechanism": "Disrupting the body's day-night clock may interfere with the brain signals that drive ovulation",
            "source": "Stocker et al., Obstetrics & Gynecology 2014 (meta-analysis, 16 cohorts, n=123,403)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/24901274/"
          },
          {
            "direction": "neutral",
            "outcome": "No randomized trial has tested whether improving sleep duration or hygiene increases pregnancy or live birth rates - the evidence is associational only",
            "source": "Caetano et al., Fertility and Sterility 2021 (systematic review, 33 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33054981/"
          }
        ],
        "headlineStat": "Sleep disruption tied to lower fertility"
      },
      "bodyFr": {
        "name": "Optimisation du sommeil pour la fertilité",
        "category": "lifestyle",
        "description": "Pratiques structurées d'hygiène du sommeil pour optimiser le rythme circadien, la production de mélatonine et l'équilibre hormonal, tous essentiels à la santé reproductive.",
        "howItWorks": "Le sommeil pilote plusieurs hormones reproductives : la mélatonine (un puissant antioxydant qui aide à protéger les ovocytes), les signaux cérébraux qui libèrent la FSH et la LH (les hormones qui déclenchent l'ovulation), l'hormone de croissance et le cortisol. Un mauvais sommeil perturbe ces rythmes et est associé à un délai plus long pour concevoir, des cycles irréguliers et une moindre réussite en FIV.",
        "evidenceStrength": "moderate",
        "forWhom": "both",
        "frequencyRange": "Tous les soirs, un rythme de sommeil régulier",
        "expectations": {
          "canExpect": [
            "Une meilleure régulation hormonale, dont mélatonine et cortisol",
            "Une meilleure résilience émotionnelle pendant le parcours fertilité",
            "Le travail posté et les perturbations du sommeil sont associés à des irrégularités menstruelles",
            "7 à 8 heures de sommeil de qualité, c'est l'objectif pour la santé reproductive"
          ],
          "shouldNotExpect": [
            "Optimiser le sommeil seul ne peut pas compenser des facteurs de fertilité majeurs",
            "Changer ses habitudes de sommeil demande des semaines de régularité avant d'avoir des effets hormonaux",
            "Les troubles du sommeil pendant les traitements de fertilité sont fréquents : sois indulgent·e avec toi-même"
          ]
        },
        "considerations": [
          "Maintiens un rythme veille-sommeil régulier, même le week-end",
          "Évite l'exposition à la lumière bleue 1 à 2 heures avant le coucher",
          "Garde la chambre fraîche (18 à 20 °C) et sombre",
          "Limite la caféine après midi et l'alcool le soir",
          "Si tu travailles en horaires décalés, parles-en à ton·ta médecin : ça peut influencer le timing du traitement"
        ],
        "bestFor": [
          "Équilibre hormonal",
          "Préparation à la FIV",
          "Travail posté",
          "Gestion du stress"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "Une revue systématique de 33 études a trouvé qu'un sommeil court, un chronotype du soir et le travail posté ou de nuit étaient associés à une moins bonne fertilité féminine et masculine et à de moins bons résultats de FIV, mais l'hétérogénéité méthodologique limite les conclusions causales fermes",
            "mechanism": "Le sommeil régule la mélatonine (antioxydant qui aide à protéger les ovocytes), les signaux cérébraux qui contrôlent la FSH et la LH (les hormones qui déclenchent l'ovulation), et le rythme quotidien du cortisol",
            "source": "Caetano et al., Fertility and Sterility 2021 (systematic review, 33 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33054981/"
          },
          {
            "direction": "negative",
            "outcome": "Une méta-analyse portant sur 123 403 femmes a montré que les travailleuses postées ont plus d'irrégularités menstruelles et d'infertilité, et que le travail de nuit est associé à un risque accru de fausse couche précoce. Les auteurs précisent toutefois que ce n'est pas suffisant pour recommander de limiter le travail posté",
            "magnitude": "Irrégularités menstruelles OR 1,22 ; infertilité OR 1,80 (brut) ; fausse couche précoce OR 1,29 (nuit)",
            "mechanism": "Perturber l'horloge jour-nuit du corps peut interférer avec les signaux cérébraux qui déclenchent l'ovulation",
            "source": "Stocker et al., Obstetrics & Gynecology 2014 (meta-analysis, 16 cohorts, n=123,403)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/24901274/"
          },
          {
            "direction": "neutral",
            "outcome": "Aucun essai randomisé n'a testé si améliorer la durée ou l'hygiène du sommeil augmente la grossesse ou la naissance vivante : les preuves restent uniquement associatives",
            "source": "Caetano et al., Fertility and Sterility 2021 (systematic review, 33 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/33054981/"
          }
        ],
        "headlineStat": "Perturbations du sommeil liées à la fertilité"
      },
      "sources": [
        {
          "title": "Sleep disturbances and fertility: a systematic review and meta-analysis",
          "journal": "Sleep Medicine Reviews",
          "year": 2020,
          "evidenceLevel": "meta-analysis"
        }
      ]
    },
    {
      "id": "therapy-toxin-reduction",
      "kind": "therapy",
      "tags": ["both-partners", "optimization", "pre-cycle", "lifestyle"],
      "title": "Environmental Toxin Reduction",
      "icon": "🌱",
      "body": {
        "name": "Environmental Toxin Reduction",
        "category": "lifestyle",
        "description": "Minimizing exposure to endocrine-disrupting chemicals (EDCs) and environmental toxins known to impair fertility, hormone balance, and embryo development.",
        "howItWorks": "Endocrine disruptors like BPA, phthalates, parabens, and pesticides mimic or block reproductive hormones. They are found in plastics, personal care products, non-stick cookware, and conventionally farmed foods. Reducing exposure can lower the body burden of these chemicals.",
        "evidenceStrength": "moderate",
        "forWhom": "both",
        "frequencyRange": "Ongoing lifestyle changes",
        "expectations": {
          "canExpect": [
            "BPA exposure is associated with lower IVF success rates in multiple studies",
            "Phthalate exposure is linked to reduced sperm quality in men",
            "Simple switches can meaningfully reduce your chemical exposure"
          ],
          "shouldNotExpect": [
            "You cannot eliminate all exposure - focus on the biggest sources first",
            "The effects of toxin reduction on fertility outcomes are hard to study directly",
            "Avoid anxiety about perfection - just reduce exposure where practical"
          ]
        },
        "considerations": [
          "Switch to glass or stainless steel food containers (avoid microwaving plastic)",
          "Choose fragrance-free personal care products and cleaning supplies",
          "Buy organic for the produce most likely to carry pesticide residues (e.g. the EWG's Dirty Dozen list) where budget allows",
          "Replace non-stick cookware with stainless steel or cast iron",
          "Filter drinking water - a simple carbon filter removes many contaminants"
        ],
        "bestFor": [
          "Pre-conception preparation",
          "Male factor (sperm quality)",
          "PMOS (ex PCOS)",
          "General reproductive health"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "In an early prospective study, higher urinary BPA concentrations in women undergoing IVF showed a dose-response trend toward more implantation failure - though the trend approached but did not reach statistical significance (p-trend=0.06)",
            "mechanism": "BPA mimics estrogen and has been shown in animal studies to disrupt folliculogenesis and oocyte maturation",
            "source": "Ehrlich et al., Environmental Health Perspectives 2012 (prospective cohort, n=137, 180 IVF cycles)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/22484414/"
          },
          {
            "direction": "neutral",
            "outcome": "A larger prospective study in the same fertility-clinic cohort found no significant association between urinary BPA and implantation, clinical pregnancy, or live birth - so the evidence for BPA harming IVF outcomes in humans is mixed",
            "source": "Mínguez-Alarcón et al., Human Reproduction 2015 (prospective cohort, n=256, 375 IVF cycles)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/26209788/"
          },
          {
            "direction": "negative",
            "outcome": "A meta-analysis of 12 studies found moderate-certainty evidence that higher levels of certain phthalate breakdown products in urine (MBP, MBzP) and DEHP in semen are associated with lower sperm concentration, though no consistent effect on sperm motility or shape",
            "mechanism": "Phthalates can block androgen activity in animal and lab studies, affecting how the testes produce testosterone and the integrity of sperm DNA",
            "source": "Wang et al., Environmental Science and Pollution Research 2023 (meta-analysis, 12 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/36504299/"
          }
        ],
        "headlineStat": "Lower sperm concentration with phthalate exposure",
        "relatedSupplements": {
          "ids": [
            "supplement-nac",
            "supplement-vitamin-c",
            "supplement-vitamin-e",
            "supplement-selenium"
          ],
          "patternNote": "The most effective strategy for toxin reduction is minimizing exposure at the source - switching to glass food containers, choosing fragrance-free products, and eating fresh over processed foods. However, your body also has its own detoxification system, and certain supplements support it. NAC boosts glutathione (your body's main detox molecule), while antioxidants like vitamins C, E, and selenium help neutralize oxidative damage caused by unavoidable exposures."
        }
      },
      "bodyFr": {
        "name": "Réduction des toxiques environnementaux",
        "category": "lifestyle",
        "description": "Limiter l'exposition aux perturbateurs endocriniens (PE) et aux toxiques environnementaux connus pour altérer la fertilité, l'équilibre hormonal et le développement embryonnaire.",
        "howItWorks": "Les perturbateurs endocriniens comme le BPA, les phtalates, les parabènes et les pesticides imitent ou bloquent les hormones reproductives. On les retrouve dans les plastiques, les produits d'hygiène, les poêles antiadhésives et les aliments issus de l'agriculture conventionnelle. Réduire l'exposition permet de baisser la charge corporelle en ces produits.",
        "evidenceStrength": "moderate",
        "forWhom": "both",
        "frequencyRange": "Changements d'hygiène de vie durables",
        "expectations": {
          "canExpect": [
            "L'exposition au BPA est associée à des taux de réussite de FIV plus bas dans plusieurs études",
            "L'exposition aux phtalates est liée à une qualité spermatique réduite chez les hommes",
            "De petits changements suffisent à réduire significativement ton exposition chimique"
          ],
          "shouldNotExpect": [
            "Tu ne peux pas éliminer toute exposition : concentre-toi sur les plus grosses sources d'abord",
            "Les effets de la réduction des toxiques sur les résultats de fertilité sont difficiles à étudier directement",
            "Évite l'anxiété de la perfection : réduis l'exposition là où c'est faisable"
          ]
        },
        "considerations": [
          "Passe aux contenants en verre ou inox (évite de chauffer le plastique au micro-ondes)",
          "Choisis des produits d'hygiène et d'entretien sans parfum",
          "Achète bio en priorité pour les fruits et légumes les plus exposés aux pesticides quand le budget le permet",
          "Remplace les poêles antiadhésives par de l'inox ou de la fonte",
          "Filtre l'eau du robinet : un simple filtre à charbon enlève beaucoup de contaminants"
        ],
        "bestFor": [
          "Préparation pré-conception",
          "Facteur masculin (qualité spermatique)",
          "SOPK (PMOS)",
          "Santé reproductive générale"
        ],
        "keyFindings": [
          {
            "direction": "negative",
            "outcome": "Dans une étude prospective ancienne, des taux urinaires de BPA plus élevés chez des femmes en FIV étaient associés à une tendance dose-effet vers plus d'échecs d'implantation, mais la tendance n'a pas atteint le seuil de significativité (p-trend = 0,06)",
            "mechanism": "Le BPA mime les œstrogènes ; les études animales ont montré qu'il perturbe la folliculogenèse et la maturation ovocytaire",
            "source": "Ehrlich et al., Environmental Health Perspectives 2012 (prospective cohort, n=137, 180 IVF cycles)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/22484414/"
          },
          {
            "direction": "neutral",
            "outcome": "Une étude prospective plus large dans la même cohorte clinique n'a trouvé aucune association significative entre le BPA urinaire et l'implantation, la grossesse clinique ou la naissance vivante. Les preuves d'un effet du BPA sur les résultats de FIV chez l'humain restent donc mitigées",
            "source": "Mínguez-Alarcón et al., Human Reproduction 2015 (prospective cohort, n=256, 375 IVF cycles)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/26209788/"
          },
          {
            "direction": "negative",
            "outcome": "Une méta-analyse de 12 études a montré des preuves de certitude modérée que des taux urinaires élevés de certains produits de dégradation des phtalates (MBP, MBzP) et de DEHP dans le sperme sont associés à une concentration spermatique plus basse, sans effet constant sur la mobilité ou la morphologie des spermatozoïdes",
            "mechanism": "Les phtalates peuvent bloquer l'activité des androgènes dans les modèles animaux et en laboratoire, en agissant sur la production de testostérone par les testicules et sur l'intégrité de l'ADN spermatique",
            "source": "Wang et al., Environmental Science and Pollution Research 2023 (meta-analysis, 12 studies)",
            "sourceUrl": "https://pubmed.ncbi.nlm.nih.gov/36504299/"
          }
        ],
        "headlineStat": "Phtalates : moins de spermatozoïdes",
        "relatedSupplements": {
          "ids": [
            "supplement-nac",
            "supplement-vitamin-c",
            "supplement-vitamin-e",
            "supplement-selenium"
          ],
          "patternNote": "La stratégie la plus efficace pour réduire les toxiques, c'est de limiter l'exposition à la source : contenants en verre, produits sans parfum, aliments frais plutôt qu'ultra-transformés. Ton corps a aussi son propre système de détoxication, et certains compléments le soutiennent. La NAC stimule le glutathion (la principale molécule de détox de l'organisme), tandis que les antioxydants comme les vitamines C, E et le sélénium aident à neutraliser les dommages oxydatifs liés aux expositions inévitables."
        }
      },
      "sources": [
        {
          "title": "Endocrine-disrupting chemicals and fertility: a systematic review",
          "journal": "Human Reproduction Update",
          "year": 2019,
          "evidenceLevel": "meta-analysis"
        },
        {
          "title": "BPA exposure and IVF outcomes: a prospective cohort study",
          "journal": "Fertility and Sterility",
          "year": 2016,
          "evidenceLevel": "cohort"
        }
      ]
    }
  ],
  "relationships": [],
  "localeVariants": []
}
